import React, { useState, useCallback, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { AdminSpeedMeasureProgressBar } from '../AdminSpeedMeasureProgressBar';
import { runAdminCheckerSpeedProbe } from '../../utils/adminProxySpeedStream';
import { isLocationCheckProxy } from '../../constants/locationCheck';
import { parseMskInstant } from '../../utils/moscow-calendar-date';
import { formatProxyExpiresAtDateOnly, formatProxyExpiresAtInfoPanelCompact, formatProxyExpiryRemainingCompact } from '../../utils/proxyExpiresDisplay';
import { createSpaLinkClickHandler } from '../../utils/spaLinkUtils';
import { proxyModifiersForType } from '../../utils/proxyModifiers';
import { useBackdropClose } from '../../hooks/useBackdropClose';
import {
  buildOsFingerprintFilterModel,
  resolveOsFingerprintColumnDisplay,
  resolveOsFingerprintFilterLabel,
} from '../../utils/osfp-display';
import { apiService } from '../../services/api';
import {
  DEFAULT_LTE_BAND_OPTIONS,
  LTE_METRIC_THRESHOLDS,
  extractApiError,
  isLteBandSupportedModemModel,
  metricQuality,
} from '../lte-band-section/LteBandSection';
import './proxy-table.css';

const DEFAULT_RF_BANDS = ['B1', 'B3', 'B7'];

const getProxyRowClientLoginForClientsPage = (row) => {
  if (!row) {
    return '';
  }
  const orig = row._original || {};
  const login = String(orig.client_login != null ? orig.client_login : row.client ?? '').trim();
  if (!login || login === '—') {
    return '';
  }
  return login;
};

/** Deep link на /network-ports: server_id / kraken_modem_id — затем строка как в колонке «Модем» (№ …). */
const buildProxyModemNetworkPortsNavigation = (row) => {
  if (!row || typeof row !== 'object') {
    return null;
  }
  const orig = row._original || row;
  const sid =
    orig.server_id != null && String(orig.server_id).trim() !== ''
      ? String(orig.server_id).trim()
      : '';
  if (sid) {
    return { mode: 'server_id', value: sid };
  }
  const kid =
    orig.kraken_modem_id != null && String(orig.kraken_modem_id).trim() !== ''
      ? String(orig.kraken_modem_id).trim()
      : '';
  if (kid) {
    return { mode: 'server_id', value: kid };
  }
  const nameRaw = orig.modem_name != null ? String(orig.modem_name).trim() : '';
  if (nameRaw.startsWith('№')) {
    return { mode: 'modem', value: nameRaw };
  }
  const ipRaw = orig.server_ip != null ? String(orig.server_ip).trim() : '';
  const oct = ipRaw ? ipRaw.split('.')[2] : '';
  const display = oct ? `№ ${oct} ${nameRaw}`.trim() : nameRaw;
  if (!display || display === '—') {
    return null;
  }
  return { mode: 'modem', value: display };
};

const getProxyKrakenModemNumberForSimManager = (row) => {
  if (!row || typeof row !== 'object') {
    return '';
  }
  const orig = row._original || row;
  if (orig.kraken_modem_id != null && String(orig.kraken_modem_id).trim() !== '') {
    return String(orig.kraken_modem_id).trim();
  }
  const nameRaw = orig.modem_name != null ? String(orig.modem_name).trim() : '';
  const mName = nameRaw.match(/№\s*(\d+)/);
  if (mName) {
    return mName[1];
  }
  const cellModem = row.modem != null ? String(row.modem).trim() : '';
  const mCell = cellModem.match(/№\s*(\d+)/);
  if (mCell) {
    return mCell[1];
  }
  return '';
};

const buildClientsPagePathForProxyRow = (row) => {
  const orig = row?._original || {};
  const clientId = orig.client_id != null ? Number(orig.client_id) : null;
  if (Number.isFinite(clientId) && clientId > 0) {
    return `/clients?client_id=${encodeURIComponent(String(clientId))}`;
  }
  const login = getProxyRowClientLoginForClientsPage(row);
  if (!login) {
    return null;
  }
  return `/clients?client_login=${encodeURIComponent(login)}`;
};

const buildNetworkPortsPathForProxyRow = (row) => {
  const spec = buildProxyModemNetworkPortsNavigation(row);
  if (!spec) {
    return null;
  }
  if (spec.mode === 'server_id') {
    return `/network-ports?modem_server_id=${encodeURIComponent(spec.value)}`;
  }
  return `/network-ports?modem=${encodeURIComponent(spec.value)}`;
};

const buildSimManagerPathForProxyRow = (row) => {
  const km = getProxyKrakenModemNumberForSimManager(row);
  if (!km) {
    return null;
  }
  const sid = getProxyModemServerIdForSimManager(row);
  const params = new URLSearchParams();
  params.set('modem_number', km);
  if (sid) {
    params.set('modem_server_id', sid);
  }
  return `/sim-manager?${params.toString()}`;
};

const getProxyModemServerIdForSimManager = (row) => {
  if (!row || typeof row !== 'object') {
    return '';
  }
  const orig = row._original || row;
  if (orig.server_id != null && String(orig.server_id).trim() !== '') {
    return String(orig.server_id).trim();
  }
  return '';
};

/**
 * ProxyTable — таблица прокси
 * По макету Figma 26-04 wa Proxy Manager (node 171-7790)
 *
 * Столбцы:
 * Checkbox, #, Клиент, Тариф, Доступность, Истекает, Страж, Сервер,
 * Модем, Порт, Тип, ГЕО, Оператор, Ротация, Действия
 */

// ==================== SVG ICONS ====================

const SortIcon = ({ active = false, direction = 'asc' }) => (
  <svg
    className={`pxt__sort-icon ${active ? 'pxt__sort-icon--active' : ''} ${direction === 'desc' ? 'pxt__sort-icon--desc' : ''}`}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6 9L12 15L18 9"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const FilterIcon = ({ active = false }) => (
  <svg
    className={`pxt__filter-icon ${active ? 'pxt__filter-icon--active' : ''}`}
    width="15"
    height="15"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9.00005 14C8.89187 14 8.7866 13.9649 8.70005 13.9L6.70005 12.4C6.63795 12.3534 6.58755 12.293 6.55284 12.2236C6.51812 12.1542 6.50005 12.0776 6.50005 12V9.19L2.99205 5.2435C2.74291 4.96244 2.58024 4.61541 2.52359 4.24412C2.46694 3.87283 2.51872 3.49308 2.67271 3.15051C2.8267 2.80794 3.07635 2.51713 3.39164 2.31303C3.70694 2.10894 4.07446 2.00024 4.45005 2H11.5501C11.9256 2.00044 12.293 2.10932 12.6082 2.31355C12.9234 2.51779 13.1729 2.80869 13.3267 3.15129C13.4805 3.4939 13.5321 3.87364 13.4753 4.24487C13.4185 4.61611 13.2558 4.96305 13.0066 5.244L9.50005 9.19V13.5C9.50005 13.6326 9.44737 13.7598 9.3536 13.8536C9.25984 13.9473 9.13266 14 9.00005 14ZM7.50005 11.75L8.50005 12.5V9C8.50015 8.87758 8.54516 8.75945 8.62655 8.668L12.2606 4.5795C12.3817 4.44254 12.4608 4.27352 12.4883 4.09273C12.5157 3.91193 12.4905 3.72705 12.4154 3.56028C12.3404 3.39351 12.2189 3.25193 12.0654 3.15254C11.9118 3.05316 11.7329 3.00019 11.5501 3H4.45005C4.26728 3.00028 4.08846 3.05327 3.93505 3.15263C3.78164 3.25199 3.66015 3.3935 3.58516 3.56018C3.51016 3.72685 3.48483 3.91163 3.51221 4.09234C3.5396 4.27305 3.61854 4.44203 3.73955 4.579L7.37405 8.668C7.45526 8.75952 7.50009 8.87764 7.50005 9V11.75Z"
      fill="currentColor" stroke="currentColor" strokeWidth="0.6"
    />
  </svg>
);

const CheckboxIcon = ({ checked = false, indeterminate = false }) => {
  if (indeterminate) {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="0.5" y="0.5" width="15" height="15" rx="1.5" stroke="#C2C2C2" fill="white" />
        <rect x="4" y="7" width="8" height="2" rx="1" fill="#5283F4" />
      </svg>
    );
  }
  if (checked) {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="0.5" y="0.5" width="15" height="15" rx="1.5" fill="#5283F4" stroke="#5283F4" />
        <path d="M4 8L7 11L12 5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0.5" y="0.5" width="15" height="15" rx="1.5" stroke="#C2C2C2" fill="white" />
    </svg>
  );
};

// Header icons
const SyncIcon = () => (
  <svg className="pxt-header__btn-icon" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5.1784 1.46003L4.4704 2.61C4.3975 2.7151 4.2871 2.7883 4.1618 2.8145C4.0366 2.8406 3.9061 2.8178 3.7972 2.7506C3.6883 2.6835 3.6093 2.5772 3.5764 2.4536C3.5435 2.3299 3.5593 2.1984 3.6204 2.086L4.3284 0.93603C4.506 0.65254 4.7526 0.41881 5.0453 0.25679C5.3379 0.09477 5.6669 0.00977 6.0014 0.00977C6.3359 0.00977 6.6649 0.09477 6.9576 0.25679C7.2502 0.41881 7.4969 0.65254 7.6744 0.93603L9.7744 4.353L9.9949 3.1705C10.0192 3.0401 10.0943 2.9247 10.2037 2.8496C10.313 2.7746 10.4478 2.746 10.5782 2.7703C10.7086 2.7945 10.824 2.8696 10.8991 2.979C10.9742 3.0884 11.0027 3.2231 10.9784 3.3535L10.7284 4.6885C10.6645 5.0322 10.4825 5.3427 10.2139 5.5665C9.9453 5.7903 9.607 5.9132 9.2574 5.914C9.1599 5.914 7.6489 5.626 7.6489 5.626C7.521 5.5982 7.4091 5.5214 7.3372 5.412C7.2654 5.3026 7.2393 5.1694 7.2645 5.0409C7.2898 4.9125 7.3644 4.7991 7.4724 4.7251C7.5803 4.651 7.713 4.6223 7.8419 4.645L8.9084 4.8555L6.8214 1.46003C6.7347 1.3203 6.6137 1.205 6.4699 1.12506C6.3262 1.04511 6.1644 1.00316 5.9999 1.00316C5.8354 1.00316 5.6737 1.04511 5.5299 1.12506C5.3862 1.205 5.2652 1.3203 5.1784 1.46003ZM11.7044 7.4895L11.2204 6.7035C11.188 6.6439 11.1437 6.5914 11.0903 6.5494C11.0368 6.5075 10.9754 6.4768 10.9098 6.4593C10.8441 6.4419 10.7756 6.438 10.7084 6.4479C10.6412 6.4578 10.5767 6.4812 10.5189 6.5169C10.461 6.5525 10.4111 6.5996 10.372 6.6552C10.333 6.7108 10.3057 6.7738 10.2918 6.8403C10.2779 6.9068 10.2777 6.9754 10.2912 7.042C10.3048 7.1086 10.3317 7.1717 10.3704 7.2275L10.8544 8.0135C10.9468 8.1614 10.9974 8.3314 11.001 8.5057C11.0046 8.68 10.9611 8.852 10.8749 9.0035C10.7921 9.1547 10.6699 9.2806 10.5213 9.3679C10.3727 9.4552 10.2033 9.5007 10.0309 9.4995H6.3379L6.9734 8.865C7.0646 8.7709 7.1152 8.6446 7.1143 8.5135C7.1133 8.3824 7.0609 8.2569 6.9683 8.1641C6.8758 8.0713 6.7504 8.0185 6.6193 8.0172C6.4882 8.0159 6.3619 8.0661 6.2674 8.157L5.4214 8.9995C5.1574 9.2621 5.0062 9.6172 4.9999 9.9895C4.9999 9.9895 4.9999 9.9945 4.9999 9.9965C4.9999 9.9985 4.9999 10.003 4.9999 10.0065C4.9985 10.1989 5.0356 10.3897 5.109 10.5675C5.1824 10.7453 5.2907 10.9067 5.4274 11.042L6.2684 11.868C6.3148 11.9163 6.3703 11.9548 6.4318 11.9813C6.4933 12.0077 6.5595 12.0215 6.6264 12.0219C6.6934 12.0223 6.7597 12.0092 6.8215 11.9835C6.8833 11.9577 6.9393 11.9198 6.9862 11.8721C7.033 11.8243 7.0698 11.7676 7.0944 11.7053C7.1189 11.643 7.1307 11.5765 7.1291 11.5095C7.1274 11.4426 7.1124 11.3767 7.0848 11.3157C7.0572 11.2548 7.0176 11.1999 6.9684 11.1545L6.3044 10.4995H10.0309C10.3817 10.4983 10.7258 10.4037 11.0279 10.2253C11.3299 10.047 11.579 9.7913 11.7494 9.4848C11.9199 9.1782 12.0055 8.8317 11.9976 8.481C11.9897 8.1304 11.8885 7.7881 11.7044 7.4895ZM3.3899 3.606C3.2261 3.4944 3.0416 3.4166 2.8473 3.3771C2.653 3.3376 2.4529 3.3373 2.2584 3.376L0.94393 3.635C0.87844 3.6466 0.81592 3.6711 0.76002 3.7071C0.70412 3.7431 0.65596 3.7899 0.61836 3.8447C0.58076 3.8995 0.55447 3.9613 0.54104 4.0264C0.52761 4.0916 0.52731 4.1587 0.54014 4.224C0.55298 4.2892 0.5787 4.3512 0.61579 4.4064C0.65289 4.4616 0.70062 4.5088 0.7562 4.5453C0.81177 4.5818 0.87406 4.6069 0.93944 4.619C1.00481 4.6312 1.07195 4.6302 1.13693 4.616L2.1909 4.4085L0.29543 7.4895C0.11135 7.7881 0.01014 8.1303 0.00221 8.4809C-0.00572 8.8316 0.07992 9.178 0.25031 9.4845C0.4207 9.7911 0.6697 10.0467 0.97167 10.2251C1.27364 10.4035 1.6177 10.4982 1.96843 10.4995H3.5399C3.6725 10.4995 3.7997 10.4469 3.8935 10.3531C3.9872 10.2593 4.0399 10.1321 4.0399 9.9995C4.0399 9.8669 3.9872 9.7397 3.8935 9.646C3.7997 9.5522 3.6725 9.4995 3.5399 9.4995H1.96843C1.79642 9.5003 1.62738 9.4547 1.47918 9.3674C1.33097 9.2801 1.2091 9.1544 1.12643 9.0035C1.0403 8.852 0.99673 8.68 1.00034 8.5057C1.00395 8.3314 1.0546 8.1614 1.14693 8.0135L3.0659 4.8945L3.2889 6.0915C3.3133 6.222 3.3885 6.3373 3.498 6.4123C3.6075 6.4873 3.7423 6.5157 3.8727 6.4913C4.0031 6.4669 4.1185 6.3917 4.1934 6.2822C4.2684 6.1727 4.2968 6.038 4.2724 5.9075L4.0224 4.5725C3.9865 4.3775 3.9121 4.1916 3.8035 4.0256C3.6949 3.8597 3.5543 3.717 3.3899 3.606Z" fill="currentColor"/>
  </svg>
);

const RefreshIcon = () => (
  <svg className="pxt-header__btn-icon" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_pxt_refresh)">
      <path d="M6.0001 0.999894C6.66336 1.00207 7.31959 1.13576 7.93084 1.39324C8.54208 1.65071 9.09619 2.02685 9.5611 2.49989H8.0001C7.86749 2.49989 7.74032 2.55257 7.64655 2.64634C7.55278 2.74011 7.5001 2.86729 7.5001 2.99989C7.5001 3.1325 7.55278 3.25968 7.64655 3.35345C7.74032 3.44722 7.86749 3.49989 8.0001 3.49989H10.0716C10.3178 3.49976 10.5539 3.4019 10.728 3.2278C10.9021 3.0537 11 2.81761 11.0001 2.57139V0.499894C11.0001 0.367286 10.9474 0.240109 10.8537 0.14634C10.7599 0.0525723 10.6327 -0.000106158 10.5001 -0.000106158C10.3675 -0.000106158 10.2403 0.0525723 10.1465 0.14634C10.0528 0.240109 10.0001 0.367286 10.0001 0.499894V1.53889C9.17369 0.79462 8.15639 0.295099 7.06215 0.0962684C5.96791 -0.102562 4.83991 0.00714982 3.80449 0.413118C2.76907 0.819085 1.86711 1.50528 1.19961 2.39486C0.532105 3.28443 0.125422 4.34227 0.0251013 5.44989C0.0186429 5.51952 0.0267591 5.58973 0.0489327 5.65605C0.0711063 5.72237 0.106851 5.78334 0.153889 5.83509C0.200928 5.88683 0.258228 5.92821 0.32214 5.95658C0.386052 5.98496 0.455174 5.99971 0.525101 5.99989C0.647395 6.00145 0.765868 5.95731 0.857331 5.87612C0.948793 5.79492 1.00666 5.68251 1.0196 5.56089C1.13091 4.31627 1.70359 3.15825 2.62514 2.31431C3.54669 1.47038 4.75051 1.00154 6.0001 0.999894Z" fill="currentColor"/>
      <path d="M11.4756 6.00004C11.3533 5.99848 11.2349 6.04262 11.1434 6.12382C11.0519 6.20501 10.9941 6.31742 10.9811 6.43904C10.8985 7.39066 10.5446 8.2986 9.96145 9.05515C9.37831 9.8117 8.5904 10.3851 7.6912 10.7073C6.79199 11.0296 5.81922 11.0871 4.8883 10.8731C3.95738 10.6591 3.10737 10.1826 2.43912 9.50005H4.00012C4.13273 9.50005 4.25991 9.44737 4.35368 9.3536C4.44744 9.25983 4.50012 9.13266 4.50012 9.00005C4.50012 8.86744 4.44744 8.74026 4.35368 8.64649C4.25991 8.55272 4.13273 8.50005 4.00012 8.50005H1.92862C1.80667 8.49998 1.6859 8.52395 1.57322 8.57059C1.46054 8.61723 1.35816 8.68562 1.27193 8.77185C1.18569 8.85808 1.1173 8.96047 1.07067 9.07315C1.02403 9.18583 1.00006 9.3066 1.00012 9.42855V11.5001C1.00012 11.6327 1.0528 11.7598 1.14657 11.8536C1.24034 11.9474 1.36751 12.0001 1.50012 12.0001C1.63273 12.0001 1.75991 11.9474 1.85368 11.8536C1.94744 11.7598 2.00012 11.6327 2.00012 11.5001V10.4611C2.82654 11.2053 3.84383 11.7048 4.93807 11.9037C6.03232 12.1025 7.16031 11.9928 8.19573 11.5868C9.23115 11.1809 10.1331 10.4947 10.8006 9.60509C11.4681 8.71551 11.8748 7.65767 11.9751 6.55004C11.9816 6.48041 11.9735 6.4102 11.9513 6.34388C11.9291 6.27756 11.8934 6.21659 11.8463 6.16485C11.7993 6.11311 11.742 6.07173 11.6781 6.04335C11.6142 6.01498 11.545 6.00023 11.4751 6.00004H11.4756Z" fill="currentColor"/>
    </g>
    <defs>
      <clipPath id="clip0_pxt_refresh">
        <rect width="12" height="12" fill="white"/>
      </clipPath>
    </defs>
  </svg>
);

const DownloadIcon = () => (
  <svg className="pxt-header__btn-icon" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 8.5C5.867 8.5 5.74 8.447 5.646 8.354L3.146 5.854C3.052 5.76 3 5.633 3 5.5C3 5.367 3.052 5.24 3.146 5.146C3.24 5.053 3.367 5 3.5 5C3.633 5 3.76 5.053 3.854 5.146L5.5 6.793V0.5C5.5 0.367 5.553 0.24 5.646 0.146C5.74 0.053 5.867 0 6 0C6.133 0 6.26 0.053 6.354 0.146C6.447 0.24 6.5 0.367 6.5 0.5V6.793L8.146 5.146C8.24 5.053 8.367 5 8.5 5C8.633 5 8.76 5.053 8.854 5.146C8.948 5.24 9 5.367 9 5.5C9 5.633 8.948 5.76 8.854 5.854L6.354 8.354C6.26 8.447 6.133 8.5 6 8.5Z" fill="currentColor"/>
    <path d="M11 8C10.867 8 10.74 8.053 10.646 8.146C10.553 8.24 10.5 8.367 10.5 8.5V10C10.5 10.265 10.395 10.52 10.207 10.707C10.02 10.895 9.765 11 9.5 11H2.5C2.235 11 1.98 10.895 1.793 10.707C1.605 10.52 1.5 10.265 1.5 10V8.5C1.5 8.367 1.447 8.24 1.354 8.146C1.26 8.053 1.133 8 1 8C0.867 8 0.74 8.053 0.646 8.146C0.553 8.24 0.5 8.367 0.5 8.5V10C0.5 10.53 0.711 11.039 1.086 11.414C1.461 11.789 1.97 12 2.5 12H9.5C10.03 12 10.539 11.789 10.914 11.414C11.289 11.039 11.5 10.53 11.5 10V8.5C11.5 8.367 11.447 8.24 11.354 8.146C11.26 8.053 11.133 8 11 8Z" fill="currentColor"/>
  </svg>
);

const FilterHeaderIcon = () => (
  <svg
    className="pxt-header__btn-icon"
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_pxt_filter_header)">
      <path
        d="M6.99999 12C6.89181 12 6.78654 11.9649 6.69999 11.9L4.69999 10.4C4.63789 10.3534 4.58749 10.293 4.55278 10.2236C4.51806 10.1542 4.49999 10.0776 4.49999 10V7.19L0.99199 3.2435C0.74285 2.96244 0.58018 2.61541 0.52353 2.24412C0.466881 1.87283 0.518662 1.49308 0.672652 1.15051C0.826641 0.807938 1.07629 0.517129 1.39158 0.313033C1.70688 0.108936 2.0744 0.000237209 2.44999 0L9.54999 0C9.92554 0.000440525 10.293 0.10932 10.6081 0.313553C10.9233 0.517787 11.1728 0.808686 11.3266 1.15129C11.4805 1.4939 11.5321 1.87364 11.4753 2.24487C11.4185 2.61611 11.2557 2.96305 11.0065 3.244L7.49999 7.19V11.5C7.49999 11.6326 7.44731 11.7598 7.35354 11.8536C7.25978 11.9473 7.1326 12 6.99999 12ZM5.49999 9.75L6.49999 10.5V7C6.50009 6.87758 6.5451 6.75945 6.62649 6.668L10.2605 2.5795C10.3817 2.44254 10.4607 2.27352 10.4882 2.09273C10.5157 1.91193 10.4904 1.72705 10.4154 1.56028C10.3404 1.39351 10.2188 1.25193 10.0653 1.15254C9.91179 1.05316 9.73286 1.00019 9.54999 1H2.44999C2.26722 1.00028 2.0884 1.05327 1.93499 1.15263C1.78158 1.25199 1.66009 1.3935 1.58509 1.56018C1.51009 1.72685 1.48477 1.91163 1.51215 2.09234C1.53954 2.27305 1.61847 2.44203 1.73949 2.579L5.37399 6.668C5.4552 6.75952 5.50003 6.87764 5.49999 7V9.75Z"
        fill="#7A7A7A"
      />
    </g>
    <defs>
      <clipPath id="clip0_pxt_filter_header">
        <rect width="12" height="12" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const GridIcon = () => (
  <svg className="pxt-header__btn-icon" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3.5 0H2C1.46957 0 0.960859 0.210714 0.585786 0.585786C0.210714 0.960859 0 1.46957 0 2V3.5C0 4.03043 0.210714 4.53914 0.585786 4.91421C0.960859 5.28929 1.46957 5.5 2 5.5H3.5C4.03043 5.5 4.53914 5.28929 4.91421 4.91421C5.28929 4.53914 5.5 4.03043 5.5 3.5V2C5.5 1.46957 5.28929 0.960859 4.91421 0.585786C4.53914 0.210714 4.03043 0 3.5 0ZM4.5 3.5C4.5 3.76522 4.39464 4.01957 4.20711 4.20711C4.01957 4.39464 3.76522 4.5 3.5 4.5H2C1.73478 4.5 1.48043 4.39464 1.29289 4.20711C1.10536 4.01957 1 3.76522 1 3.5V2C1 1.73478 1.10536 1.48043 1.29289 1.29289C1.48043 1.10536 1.73478 1 2 1H3.5C3.76522 1 4.01957 1.10536 4.20711 1.29289C4.39464 1.48043 4.5 1.73478 4.5 2V3.5Z" fill="currentColor"/>
    <path d="M10 0H8.5C7.96957 0 7.46086 0.210714 7.08579 0.585786C6.71071 0.960859 6.5 1.46957 6.5 2V3.5C6.5 4.03043 6.71071 4.53914 7.08579 4.91421C7.46086 5.28929 7.96957 5.5 8.5 5.5H10C10.5304 5.5 11.0391 5.28929 11.4142 4.91421C11.7893 4.53914 12 4.03043 12 3.5V2C12 1.46957 11.7893 0.960859 11.4142 0.585786C11.0391 0.210714 10.5304 0 10 0ZM11 3.5C11 3.76522 10.8946 4.01957 10.7071 4.20711C10.5196 4.39464 10.2652 4.5 10 4.5H8.5C8.23478 4.5 7.98043 4.39464 7.79289 4.20711C7.60536 4.01957 7.5 3.76522 7.5 3.5V2C7.5 1.73478 7.60536 1.48043 7.79289 1.29289C7.98043 1.10536 8.23478 1 8.5 1H10C10.2652 1 10.5196 1.10536 10.7071 1.29289C10.8946 1.48043 11 1.73478 11 2V3.5Z" fill="currentColor"/>
    <path d="M3.5 6.5H2C1.46957 6.5 0.960859 6.71071 0.585786 7.08579C0.210714 7.46086 0 7.96957 0 8.5V10C0 10.5304 0.210714 11.0391 0.585786 11.4142C0.960859 11.7893 1.46957 12 2 12H3.5C4.03043 12 4.53914 11.7893 4.91421 11.4142C5.28929 11.0391 5.5 10.5304 5.5 10V8.5C5.5 7.96957 5.28929 7.46086 4.91421 7.08579C4.53914 6.71071 4.03043 6.5 3.5 6.5ZM4.5 10C4.5 10.2652 4.39464 10.5196 4.20711 10.7071C4.01957 10.8946 3.76522 11 3.5 11H2C1.73478 11 1.48043 10.8946 1.29289 10.7071C1.10536 10.5196 1 10.2652 1 10V8.5C1 8.23478 1.10536 7.98043 1.29289 7.79289C1.48043 7.60536 1.73478 7.5 2 7.5H3.5C3.76522 7.5 4.01957 7.60536 4.20711 7.79289C4.39464 7.98043 4.5 8.23478 4.5 8.5V10Z" fill="currentColor"/>
    <path d="M10 6.5H8.5C7.96957 6.5 7.46086 6.71071 7.08579 7.08579C6.71071 7.46086 6.5 7.96957 6.5 8.5V10C6.5 10.5304 6.71071 11.0391 7.08579 11.4142C7.46086 11.7893 7.96957 12 8.5 12H10C10.5304 12 11.0391 11.7893 11.4142 11.4142C11.7893 11.0391 12 10.5304 12 10V8.5C12 7.96957 11.7893 7.46086 11.4142 7.08579C11.0391 6.71071 10.5304 6.5 10 6.5ZM11 10C11 10.2652 10.8946 10.5196 10.7071 10.7071C10.5196 10.8946 10.2652 11 10 11H8.5C8.23478 11 7.98043 10.8946 7.79289 10.7071C7.60536 10.5196 7.5 10.2652 7.5 10V8.5C7.5 8.23478 7.60536 7.98043 7.79289 7.79289C7.98043 7.60536 8.23478 7.5 8.5 7.5H10C10.2652 7.5 10.5196 7.60536 10.7071 7.79289C10.8946 7.98043 11 8.23478 11 8.5V10Z" fill="currentColor"/>
  </svg>
);

const PlusIcon = () => (
  <svg className="pxt-header__btn-icon" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11.5 5.5H6.5V0.5C6.5 0.367 6.447 0.24 6.354 0.146C6.26 0.053 6.133 0 6 0C5.867 0 5.74 0.053 5.646 0.146C5.553 0.24 5.5 0.367 5.5 0.5V5.5H0.5C0.367 5.5 0.24 5.553 0.146 5.646C0.053 5.74 0 5.867 0 6C0 6.133 0.053 6.26 0.146 6.354C0.24 6.447 0.367 6.5 0.5 6.5H5.5V11.5C5.5 11.633 5.553 11.76 5.646 11.854C5.74 11.947 5.867 12 6 12C6.133 12 6.26 11.947 6.354 11.854C6.447 11.76 6.5 11.633 6.5 11.5V6.5H11.5C11.633 6.5 11.76 6.447 11.854 6.354C11.947 6.26 12 6.133 12 6C12 5.867 11.947 5.74 11.854 5.646C11.76 5.553 11.633 5.5 11.5 5.5Z" fill="currentColor"/>
  </svg>
);

// Selection action icons (from Figma SVG)
const TrashIcon = () => (
  <svg className="pxt-header__btn-icon" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10.5 2H8.95C8.83 1.4357 8.53 0.9287 8.08 0.5644C7.63 0.2 7.08 0.0007 6.5 0H5.5C4.92 0.0007 4.37 0.2 3.92 0.5644C3.47 0.9287 3.17 1.4357 3.05 2H1.5C1.37 2 1.24 2.0527 1.15 2.1464C1.05 2.2402 1 2.3674 1 2.5C1 2.6326 1.05 2.7598 1.15 2.8536C1.24 2.9473 1.37 3 1.5 3H2V9.5C2 10.1628 2.26 10.7982 2.73 11.2669C3.2 11.7356 3.84 11.9992 4.5 12H7.5C8.16 11.9992 8.8 11.7356 9.27 11.2669C9.74 10.7982 10 10.1628 10 9.5V3H10.5C10.63 3 10.76 2.9473 10.85 2.8536C10.95 2.7598 11 2.6326 11 2.5C11 2.3674 10.95 2.2402 10.85 2.1464C10.76 2.0527 10.63 2 10.5 2ZM5.5 1H6.5C6.81 1.0004 7.11 1.0967 7.37 1.2757C7.62 1.4548 7.81 1.7077 7.91 2H4.09C4.19 1.7077 4.38 1.4548 4.63 1.2757C4.89 1.0967 5.19 1.0004 5.5 1ZM9 9.5C9 9.8978 8.84 10.2794 8.56 10.5607C8.28 10.842 7.9 11 7.5 11H4.5C4.1 11 3.72 10.842 3.44 10.5607C3.16 10.2794 3 9.8978 3 9.5V3H9V9.5Z" fill="currentColor"/>
    <path d="M5 8.9996C5.13 8.9996 5.26 8.9469 5.35 8.8532C5.45 8.7594 5.5 8.6322 5.5 8.4996V5.4996C5.5 5.367 5.45 5.2398 5.35 5.1461C5.26 5.0523 5.13 4.9996 5 4.9996C4.87 4.9996 4.74 5.0523 4.65 5.1461C4.55 5.2398 4.5 5.367 4.5 5.4996V8.4996C4.5 8.6322 4.55 8.7594 4.65 8.8532C4.74 8.9469 4.87 8.9996 5 8.9996Z" fill="currentColor"/>
    <path d="M7 8.9996C7.13 8.9996 7.26 8.9469 7.35 8.8532C7.45 8.7594 7.5 8.6322 7.5 8.4996V5.4996C7.5 5.367 7.45 5.2398 7.35 5.1461C7.26 5.0523 7.13 4.9996 7 4.9996C6.87 4.9996 6.74 5.0523 6.65 5.1461C6.55 5.2398 6.5 5.367 6.5 5.4996V8.4996C6.5 8.6322 6.55 8.7594 6.65 8.8532C6.74 8.9469 6.87 8.9996 7 8.9996Z" fill="currentColor"/>
  </svg>
);

/** Иконка «отключить» (power) для массового отключения прокси */
const PowerOffHeaderIcon = () => (
  <svg className="pxt-header__btn-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M12 3v9"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M18.36 6.64a9 9 0 1 1-12.73 0"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

/** Та же пиктограмма питания — для «включить» отличается только класс кнопки (зелёный). */
const PowerOnHeaderIcon = () => (
  <svg className="pxt-header__btn-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M12 3v9"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M18.36 6.64a9 9 0 1 1-12.73 0"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const EditIcon = () => (
  <svg className="pxt-header__btn-icon" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.33 0.4652L3.23 6.5612C3 6.7927 2.81 7.0682 2.69 7.3716C2.56 7.675 2.5 8.0003 2.5 8.3287V9.0002C2.5 9.1328 2.55 9.2599 2.65 9.3537C2.74 9.4475 2.87 9.5002 3 9.5002H3.67C4 9.5011 4.33 9.4369 4.63 9.3112C4.93 9.1856 5.21 9.001 5.44 8.7682L11.54 2.6722C11.83 2.3792 11.99 1.9824 11.99 1.5687C11.99 1.1549 11.83 0.7581 11.54 0.4652C11.24 0.1811 10.84 0.0226 10.43 0.0226C10.02 0.0226 9.63 0.1811 9.33 0.4652ZM10.83 1.9652L4.73 8.0612C4.45 8.3414 4.07 8.4991 3.67 8.5002H3.5V8.3287C3.5 7.9312 3.66 7.5501 3.94 7.2682L10.04 1.1722C10.14 1.0701 10.28 1.0132 10.43 1.0132C10.58 1.0132 10.72 1.0701 10.83 1.1722C10.93 1.2774 10.99 1.42 10.99 1.5687C10.99 1.7173 10.93 1.8599 10.83 1.9652Z" fill="currentColor"/>
    <path d="M11.5 4.4895C11.37 4.4895 11.24 4.5422 11.15 4.6359C11.05 4.7297 11 4.8569 11 4.9895V7.5H9C8.6 7.5 8.22 7.658 7.94 7.9393C7.66 8.2206 7.5 8.6022 7.5 9V11H2.5C2.1 11 1.72 10.842 1.44 10.5607C1.16 10.2794 1 9.8978 1 9.5V2.5C1 2.1022 1.16 1.7206 1.44 1.4393C1.72 1.158 2.1 1 2.5 1H7.02C7.15 1 7.28 0.9473 7.37 0.8536C7.47 0.7598 7.52 0.6326 7.52 0.5C7.52 0.3674 7.47 0.2402 7.37 0.1464C7.28 0.0527 7.15 0 7.02 0H2.5C1.84 0.0008 1.2 0.2644 0.73 0.7331C0.26 1.2018 0 1.8372 0 2.5V9.5C0 10.1628 0.26 10.7982 0.73 11.2669C1.2 11.7356 1.84 11.9992 2.5 12H8.17C8.5 12.0009 8.83 11.9367 9.13 11.8111C9.43 11.6854 9.71 11.5008 9.94 11.268L11.27 9.939C11.5 9.7074 11.69 9.432 11.81 9.1286C11.94 8.8252 12 8.4999 12 8.1715V4.9895C12 4.8569 11.95 4.7297 11.85 4.6359C11.76 4.5422 11.63 4.4895 11.5 4.4895ZM9.23 10.561C9.03 10.7615 8.78 10.9003 8.5 10.961V9C8.5 8.8674 8.55 8.7402 8.65 8.6464C8.74 8.5527 8.87 8.5 9 8.5H10.96C10.9 8.7768 10.76 9.0305 10.56 9.232L9.23 10.561Z" fill="currentColor"/>
  </svg>
);

/** Иконка «добавить в чекер» (список + плюс), цвет задаётся кнопкой */
const AddToCheckerIcon = () => (
  <svg className="pxt-header__btn-icon" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M2 1.5C1.72386 1.5 1.5 1.72386 1.5 2V10C1.5 10.2761 1.72386 10.5 2 10.5H7.5V11.5H2C1.17157 11.5 0.5 10.8284 0.5 10V2C0.5 1.17157 1.17157 0.5 2 0.5H8C8.82843 0.5 9.5 1.17157 9.5 2V6.5H8.5V2C8.5 1.72386 8.27614 1.5 8 1.5H2Z"
      fill="currentColor"
    />
    <path
      d="M10.25 7.5H11.25V9.25H13V10.25H11.25V12H10.25V10.25H8.5V9.25H10.25V7.5Z"
      fill="currentColor"
    />
  </svg>
);

/** Иконка «в проверку локаций» — map pin */
const LocationCheckIcon = () => (
  <svg className="pxt-header__btn-icon" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M6 0C3.79086 0 2 1.79086 2 4C2 6.76142 5.25 11.25 5.55 11.625C5.66667 11.7708 5.83333 11.85 6 11.85C6.16667 11.85 6.33333 11.7708 6.45 11.625C6.75 11.25 10 6.76142 10 4C10 1.79086 8.20914 0 6 0ZM6 5.5C6.82843 5.5 7.5 4.82843 7.5 4C7.5 3.17157 6.82843 2.5 6 2.5C5.17157 2.5 4.5 3.17157 4.5 4C4.5 4.82843 5.17157 5.5 6 5.5Z"
      fill="currentColor"
    />
  </svg>
);

const BugIcon = () => (
  <svg className="pxt-header__btn-icon" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#pxt-check-clip)">
      <path d="M11 3.5C11.2652 3.5 11.5196 3.39464 11.7071 3.20711C11.8946 3.01957 12 2.76522 12 2.5V0.5C12 0.367392 11.9473 0.240215 11.8536 0.146447C11.7598 0.0526784 11.6326 0 11.5 0C11.3674 0 11.2402 0.0526784 11.1464 0.146447C11.0527 0.240215 11 0.367392 11 0.5V2.5H9C8.991 2.5 8.984 2.5045 8.975 2.505C8.73585 1.9125 8.32509 1.40499 7.79545 1.04761C7.2658 0.690226 6.64144 0.499281 6.0025 0.499281C5.36356 0.499281 4.7392 0.690226 4.20955 1.04761C3.67991 1.40499 3.26915 1.9125 3.03 2.505C3.0195 2.5055 3.0105 2.5 3 2.5H1V0.5C1 0.367392 0.947322 0.240215 0.853553 0.146447C0.759785 0.0526784 0.632608 0 0.5 0C0.367392 0 0.240215 0.0526784 0.146447 0.146447C0.0526784 0.240215 0 0.367392 0 0.5L0 2.5C0 2.76522 0.105357 3.01957 0.292893 3.20711C0.48043 3.39464 0.734784 3.5 1 3.5H2.7425L2.236 5.5H0.5C0.367392 5.5 0.240215 5.55268 0.146447 5.64645C0.0526784 5.74021 0 5.86739 0 6C0 6.13261 0.0526784 6.25979 0.146447 6.35355C0.240215 6.44732 0.367392 6.5 0.5 6.5H1.9825L1.91 6.7865L1.9 6.85C1.87771 7.02111 1.86602 7.19344 1.865 7.366C1.86551 7.74969 1.91987 8.13142 2.0265 8.5H1C0.734784 8.5 0.48043 8.60536 0.292893 8.79289C0.105357 8.98043 0 9.23478 0 9.5L0 11.5C0 11.6326 0.0526784 11.7598 0.146447 11.8536C0.240215 11.9473 0.367392 12 0.5 12C0.632608 12 0.759785 11.9473 0.853553 11.8536C0.947322 11.7598 1 11.6326 1 11.5V9.5H2.465C2.83155 10.1101 3.34976 10.6149 3.96921 10.9654C4.58866 11.3159 5.28827 11.5001 6 11.5001C6.71173 11.5001 7.41134 11.3159 8.03079 10.9654C8.65024 10.6149 9.16845 10.1101 9.535 9.5H11V11.5C11 11.6326 11.0527 11.7598 11.1464 11.8536C11.2402 11.9473 11.3674 12 11.5 12C11.6326 12 11.7598 11.9473 11.8536 11.8536C11.9473 11.7598 12 11.6326 12 11.5V9.5C12 9.23478 11.8946 8.98043 11.7071 8.79289C11.5196 8.60536 11.2652 8.5 11 8.5H9.9735C10.0807 8.13066 10.1354 7.74808 10.136 7.3635C10.1345 7.19176 10.1225 7.02027 10.1 6.85L10.0125 6.5H11.5C11.6326 6.5 11.7598 6.44732 11.8536 6.35355C11.9473 6.25979 12 6.13261 12 6C12 5.86739 11.9473 5.74021 11.8536 5.64645C11.7598 5.55268 11.6326 5.5 11.5 5.5H9.761L9.2565 3.5H11ZM6 10.5C5.16848 10.4991 4.37129 10.1683 3.78336 9.58029C3.19543 8.99227 2.86479 8.19502 2.864 7.3635C2.86469 7.24198 2.87253 7.1206 2.8875 7L3.9025 2.985C4.05483 2.55255 4.33709 2.17775 4.71065 1.91191C5.0842 1.64606 5.53079 1.50217 5.98928 1.49992C6.44777 1.49768 6.89575 1.63719 7.27189 1.89937C7.64803 2.16154 7.93394 2.53356 8.0905 2.9645L9.1125 7C9.12743 7.12027 9.13527 7.24131 9.136 7.3625C9.13547 8.19419 8.80495 8.9917 8.21699 9.57993C7.62904 10.1682 6.83169 10.4991 6 10.5Z" fill="currentColor"/>
    </g>
    <defs>
      <clipPath id="pxt-check-clip">
        <rect width="12" height="12" fill="white"/>
      </clipPath>
    </defs>
  </svg>
);

// Filter-off (reset all filters) icon — funnel with an X
const FilterOffIcon = () => (
  <svg className="pxt-header__btn-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 4H15.5L9 13V19L7 17V13L3 7.58V4Z" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M17 8L22 13" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
    <path d="M22 8L17 13" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
  </svg>
);

// Pagination icons
const ChevronDoubleLeft = () => (
  <svg className="pxt__pagination-btn-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11 17L6 12L11 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M18 17L13 12L18 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronLeft = () => (
  <svg className="pxt__pagination-btn-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronRight = () => (
  <svg className="pxt__pagination-btn-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronDoubleRight = () => (
  <svg className="pxt__pagination-btn-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13 7L18 12L13 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M6 7L11 12L6 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronDown = () => (
  <svg className="pxt__pagination-select-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ==================== COLUMN DEFINITIONS ====================

const COLUMNS = [
  { key: 'client', label: 'Клиент', sortable: true, filterable: true },
  { key: 'tariff', label: 'Тариф', sortable: true, filterable: true },
  { key: 'availability', label: 'Доступность', sortable: true, filterable: true },
  { key: 'expires_at', label: 'Истекает', sortable: true, filterable: true },
  { key: 'guard', label: 'Страж', sortable: true, filterable: true },
  { key: 'server', label: 'Сервер', sortable: true, filterable: true },
  { key: 'modem', label: 'Модем', sortable: true, filterable: true },
  { key: 'port', label: 'Порт', sortable: true, filterable: true },
  { key: 'type', label: 'Тип', sortable: true, filterable: true },
  { key: 'geo', label: 'ГЕО', sortable: true, filterable: true },
  { key: 'operator', label: 'Оператор', sortable: true, filterable: true },
  { key: 'osFingerprint', label: 'Отпечаток ОС', sortable: true, filterable: true },
  { key: 'rotation', label: 'Ротация', sortable: true, filterable: true },
];

const ROTATION_LABELS = {
  none: 'По умолчанию',
  time: 'Время',
  link: 'Ссылка',
  time_link: 'Время и Ссылка',
  timelink: 'Время и Ссылка',
};

const getRotationLabel = (raw) => {
  if (!raw) return '—';
  const key = raw.toLowerCase().replace(/[\s-]+/g, '_');
  return ROTATION_LABELS[key] || raw;
};

// ==================== STATUS HEADER ICON (half green / half red) ====================

const StatusHeaderIcon = () => (
  <svg className="pxt__status-header-icon" width="10" height="10" viewBox="0 0 10 10" xmlns="http://www.w3.org/2000/svg">
    <path d="M5 0 A5 5 0 0 0 5 10 Z" fill="#0cbe90" />
    <path d="M5 0 A5 5 0 0 1 5 10 Z" fill="#f33f4e" />
  </svg>
);

// ==================== STATUS DOT HELPER ====================

const isProxyActive = (row) => {
  const orig = row._original || row;
  // is_active — поле БД: включён/выключён прокси
  if (orig.is_active !== undefined && orig.is_active !== null) {
    return Boolean(orig.is_active);
  }
  return false;
};

// ==================== BADGE HELPERS ====================

const getTariffBadge = (value) => {
  if (!value) return null;
  const v = value.toLowerCase();
  if (v.includes('общий') || v === 'shared') {
    return <span className="pxt__badge pxt__badge--shared">{value}</span>;
  }
  if (v.includes('индивид') || v === 'individual') {
    return <span className="pxt__badge pxt__badge--individual">{value}</span>;
  }
  return <span className="pxt__badge pxt__badge--shared">{value}</span>;
};

const getAvailabilityBadge = (value) => {
  if (!value) return null;
  const v = value.toLowerCase();
  if (v.includes('доступен') && !v.includes('не')) {
    return <span className="pxt__badge pxt__badge--available">{value}</span>;
  }
  if (v.includes('недоступ') || v.includes('не доступ')) {
    return <span className="pxt__badge pxt__badge--unavailable">{value}</span>;
  }
  if (v.includes('нет данных') || v.includes('no data')) {
    return <span className="pxt__badge pxt__badge--nodata">{value}</span>;
  }
  return <span className="pxt__badge pxt__badge--nodata">{value}</span>;
};

const normalizeAvailabilityFilterValue = (value) => {
  if (value === undefined || value === null) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  const normalized = raw.toLowerCase();

  if (
    normalized === 'none' ||
    normalized === 'null' ||
    normalized === 'no data' ||
    normalized === 'нет данных' ||
    normalized === 'неизвестно'
  ) {
    return 'none';
  }

  if (normalized === 'unavailable' || normalized.includes('недоступ')) {
    return 'Недоступен';
  }

  if (normalized === 'available' || (normalized.includes('доступ') && !normalized.includes('недоступ'))) {
    return 'Доступен';
  }

  return raw;
};

const getAvailabilityFilterDisplayValue = (value) => {
  const normalized = normalizeAvailabilityFilterValue(value);
  if (!normalized) return '';
  if (normalized === 'none') return 'Нет данных';
  return normalized;
};

const normalizeStatusFilterValue = (value) => {
  if (value === undefined || value === null) return null;
  if (typeof value === 'boolean') return value;

  const normalized = String(value).trim().toLowerCase();
  if (!normalized) return null;

  if (normalized === 'true' || normalized === '1' || normalized === 'active' || normalized === 'включены' || normalized === 'активно') {
    return true;
  }
  if (normalized === 'false' || normalized === '0' || normalized === 'inactive' || normalized === 'выключены' || normalized === 'неактивно') {
    return false;
  }
  return null;
};

const getStatusFilterDisplayValue = (value) => {
  const normalized = normalizeStatusFilterValue(value);
  if (normalized === true) return 'true';
  if (normalized === false) return 'false';
  return null;
};

const normalizeGuardFilterValue = (value) => {
  if (value === undefined || value === null) return null;
  if (typeof value === 'boolean') return value ? 'Вкл' : 'Выкл';

  const normalized = String(value).trim().toLowerCase();
  if (!normalized) return null;

  if (normalized === 'true' || normalized === '1' || normalized === 'on' || normalized === 'вкл' || normalized === 'включен' || normalized === 'включено') {
    return 'Вкл';
  }
  if (normalized === 'false' || normalized === '0' || normalized === 'off' || normalized === 'выкл' || normalized === 'выключен' || normalized === 'выключено') {
    return 'Выкл';
  }
  return null;
};

const getGuardFilterDisplayValue = (value) => normalizeGuardFilterValue(value) || '';

const getGuardBadge = (value) => {
  if (!value) return null;
  const v = value.toLowerCase();
  if (v === 'вкл' || v === 'on' || v === 'true' || v === '1') {
    return <span className="pxt__badge pxt__badge--on">Вкл</span>;
  }
  if (v === 'выкл' || v === 'off' || v === 'false' || v === '0') {
    return <span className="pxt__badge pxt__badge--off">Выкл</span>;
  }
  return <span className="pxt__badge pxt__badge--on">{value}</span>;
};

const getOperatorBadge = (value) => {
  if (!value) return null;
  const v = value.toLowerCase();
  if (v === 'unknown' || v === 'none' || v === 'null' || v === 'n/a') {
    return <span className="pxt__badge pxt__badge--operator pxt__badge--operator-default">Неизвестно</span>;
  }
  if (v.includes('мтс') || v === 'mts') {
    return <span className="pxt__badge pxt__badge--operator pxt__badge--mts">{value}</span>;
  }
  if (v.includes('мегафон') || v === 'megafon') {
    return <span className="pxt__badge pxt__badge--operator pxt__badge--megafon">{value}</span>;
  }
  if (v.includes('т2') || v.includes('теле2') || v === 'tele2') {
    return <span className="pxt__badge pxt__badge--operator pxt__badge--tele2">{value}</span>;
  }
  if (v.includes('билайн') || v === 'beeline') {
    return <span className="pxt__badge pxt__badge--operator pxt__badge--beeline">{value}</span>;
  }
  return <span className="pxt__badge pxt__badge--operator pxt__badge--operator-default">{value}</span>;
};

// ==================== FILTER DROPDOWN ====================

const ColumnFilterDropdown = ({ column, title, values, activeFilters, onApply, onReset, onClose, labelMap = {}, triggerEl = null }) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set(activeFilters || []));
  const dropdownRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({
    top: 0,
    left: 0,
    width: null,
    maxHeight: null,
    visibility: 'hidden',
  });

  useEffect(() => {
    const handleClickOutside = (e) => {
      const clickedInsideDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
      const clickedTrigger = triggerEl && triggerEl.contains && triggerEl.contains(e.target);
      if (!clickedInsideDropdown && !clickedTrigger) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, triggerEl]);

  const updatePosition = useCallback(() => {
    if (!triggerEl) return;
    const rect = triggerEl.getBoundingClientRect();
    const dropdownNode = dropdownRef.current;
    const viewportPadding = 8;
    const verticalGap = 4;

    // Фиксируем ширину dropdown и не пересчитываем её от контента при скролле,
    // иначе длинные значения "раздувают" окно фильтра.
    const preferredWidth = Math.min(320, Math.max(220, Math.round(rect.width + 80)));
    const measuredWidth = dropdownStyle.width || preferredWidth;
    const measuredHeight = dropdownNode?.offsetHeight || 260;
    const maxWidth = Math.max(200, window.innerWidth - viewportPadding * 2);
    const maxHeight = Math.max(220, window.innerHeight - viewportPadding * 2);
    const dropdownWidth = Math.min(measuredWidth, maxWidth);
    const dropdownHeight = Math.min(measuredHeight, maxHeight);

    let left = rect.left;
    let top = rect.bottom + verticalGap;

    if (left + dropdownWidth > window.innerWidth - viewportPadding) {
      left = window.innerWidth - dropdownWidth - viewportPadding;
    }
    if (left < viewportPadding) {
      left = viewportPadding;
    }

    if (top + dropdownHeight > window.innerHeight - viewportPadding) {
      top = rect.top - dropdownHeight - verticalGap;
    }
    if (top < viewportPadding) {
      top = viewportPadding;
    }

    setDropdownStyle({
      top,
      left,
      width: dropdownWidth,
      maxHeight,
      visibility: 'visible',
    });
  }, [triggerEl, dropdownStyle.width]);

  useEffect(() => {
    const rafId = requestAnimationFrame(updatePosition);
    window.addEventListener('resize', updatePosition);
    // capture:true чтобы реагировать на скролл любых вложенных контейнеров
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [updatePosition]);

  const filtered = values.filter((v) => {
    const label = labelMap[v] || String(v);
    return label.toLowerCase().includes(search.toLowerCase());
  });

  const handleToggle = (val) => {
    if (column === 'availability') {
      if (selected.has(val)) {
        setSelected(new Set());
      } else {
        setSelected(new Set([val]));
      }
      return;
    }
    const next = new Set(selected);
    if (next.has(val)) {
      next.delete(val);
    } else {
      next.add(val);
    }
    setSelected(next);
  };

  const handleApply = () => {
    onApply(column, Array.from(selected));
    onClose();
  };

  const handleReset = () => {
    setSelected(new Set());
    onReset(column);
    onClose();
  };

  const dropdownNode = (
    <div className="pxt__filter-dropdown" ref={dropdownRef}>
      <div className="pxt__filter-dropdown-header">Фильтр: {title || column}</div>
      <div className="pxt__filter-dropdown-search">
        <input
          className="pxt__filter-dropdown-search-input"
          placeholder="Поиск..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
        />
      </div>
      <div className="pxt__filter-dropdown-list">
        {filtered.length === 0 && (
          <div className="pxt__filter-dropdown-empty">Ничего не найдено</div>
        )}
        {filtered.map((val) => (
          <label className="pxt__filter-dropdown-item" key={val}>
            <input
              type="checkbox"
              checked={selected.has(val)}
              onChange={() => handleToggle(val)}
            />
            <span className="pxt__filter-dropdown-label">{labelMap[val] || val}</span>
          </label>
        ))}
      </div>
      <div className="pxt__filter-dropdown-actions">
        <button
          type="button"
          className="pxt__filter-dropdown-btn pxt__filter-dropdown-btn--reset"
          onClick={handleReset}
        >
          Сбросить
        </button>
        <button
          type="button"
          className="pxt__filter-dropdown-btn pxt__filter-dropdown-btn--apply"
          onClick={handleApply}
        >
          Применить
        </button>
      </div>
    </div>
  );

  return ReactDOM.createPortal(
    React.cloneElement(dropdownNode, {
      style: {
        position: 'fixed',
        top: dropdownStyle.top,
        left: dropdownStyle.left,
        width: dropdownStyle.width || undefined,
        maxWidth: `calc(100vw - 16px)`,
        maxHeight: dropdownStyle.maxHeight || `calc(100vh - 16px)`,
        transform: 'none',
        overflow: 'hidden',
        marginTop: 0,
        zIndex: 6000,
        visibility: dropdownStyle.visibility,
      },
    }),
    document.body
  );
};

const EXPIRES_AT_FILTER_UNLIMITED = 'unlimited';

/** Разбор значения фильтра expires_at из state (как в convertFiltersToApiParams). */
const parseExpiresAtFilterState = (activeFilters) => {
  if (!activeFilters || activeFilters.length === 0) {
    return { from: '', to: '', unlimited: false };
  }

  const unlimited = activeFilters.includes(EXPIRES_AT_FILTER_UNLIMITED);
  const dateEntry = activeFilters.find((item) => item !== EXPIRES_AT_FILTER_UNLIMITED);

  if (!dateEntry) {
    return { from: '', to: '', unlimited };
  }

  if (Array.isArray(dateEntry)) {
    const fromRaw = dateEntry[0];
    const toRaw = dateEntry[1];
    return {
      from: fromRaw != null && String(fromRaw).trim() !== ''
        ? String(fromRaw).slice(0, 10)
        : '',
      to: toRaw != null && String(toRaw).trim() !== ''
        ? String(toRaw).slice(0, 10)
        : '',
      unlimited,
    };
  }

  const single = String(dateEntry).slice(0, 10);
  return { from: single, to: single, unlimited };
};

/** Фильтр по диапазону дат истечения (сервер: expires_at_from / expires_at_to). */
const ExpiresAtFilterDropdown = ({
  activeFilters,
  onApply,
  onReset,
  onClose,
  triggerEl,
  title = 'Истекает',
}) => {
  const initial = parseExpiresAtFilterState(activeFilters);
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [unlimited, setUnlimited] = useState(initial.unlimited);
  const dropdownRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({
    top: 0,
    left: 0,
    width: null,
    maxHeight: null,
    visibility: 'hidden',
  });

  useEffect(() => {
    const next = parseExpiresAtFilterState(activeFilters);
    setFrom(next.from);
    setTo(next.to);
    setUnlimited(next.unlimited);
  }, [activeFilters]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const clickedInsideDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
      const clickedTrigger = triggerEl && triggerEl.contains && triggerEl.contains(e.target);
      if (!clickedInsideDropdown && !clickedTrigger) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, triggerEl]);

  const updatePosition = useCallback(() => {
    if (!triggerEl) return;
    const rect = triggerEl.getBoundingClientRect();
    const dropdownNode = dropdownRef.current;
    const viewportPadding = 8;
    const verticalGap = 4;
    const preferredWidth = Math.min(320, Math.max(260, Math.round(rect.width + 120)));
    const measuredWidth = dropdownStyle.width || preferredWidth;
    const measuredHeight = dropdownNode?.offsetHeight || 220;
    const maxWidth = Math.max(240, window.innerWidth - viewportPadding * 2);
    const maxHeight = Math.max(200, window.innerHeight - viewportPadding * 2);
    const dropdownWidth = Math.min(measuredWidth, maxWidth);
    const dropdownHeight = Math.min(measuredHeight, maxHeight);
    let left = rect.left;
    let top = rect.bottom + verticalGap;
    if (left + dropdownWidth > window.innerWidth - viewportPadding) {
      left = window.innerWidth - dropdownWidth - viewportPadding;
    }
    if (left < viewportPadding) {
      left = viewportPadding;
    }
    if (top + dropdownHeight > window.innerHeight - viewportPadding) {
      top = rect.top - dropdownHeight - verticalGap;
    }
    if (top < viewportPadding) {
      top = viewportPadding;
    }
    setDropdownStyle({
      top,
      left,
      width: dropdownWidth,
      maxHeight,
      visibility: 'visible',
    });
  }, [triggerEl, dropdownStyle.width]);

  useEffect(() => {
    const rafId = requestAnimationFrame(updatePosition);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [updatePosition]);

  const handleApply = () => {
    const f = String(from || '').trim();
    const t = String(to || '').trim();
    const nextFilters = [];

    if (unlimited) {
      nextFilters.push(EXPIRES_AT_FILTER_UNLIMITED);
    }

    if (f && t) {
      if (f === t) {
        nextFilters.push(f);
      } else {
        const lo = f <= t ? f : t;
        const hi = f <= t ? t : f;
        nextFilters.push([lo, hi]);
      }
    } else if (f && !t) {
      nextFilters.push([f, '']);
    } else if (!f && t) {
      nextFilters.push(['', t]);
    }

    onApply('expires_at', nextFilters);
    onClose();
  };

  const handleReset = () => {
    setFrom('');
    setTo('');
    setUnlimited(false);
    onReset('expires_at');
    onClose();
  };

  const handleUnlimitedChange = (e) => {
    setUnlimited(e.target.checked);
  };

  const handleUnlimitedKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setUnlimited((prev) => !prev);
    }
  };

  const dropdownNode = (
    <div
      className="pxt__filter-dropdown pxt__filter-dropdown--dates"
      ref={dropdownRef}
      role="dialog"
      aria-modal="true"
      aria-label={`Фильтр: ${title}`}
    >
      <div className="pxt__filter-dropdown-header">Фильтр: {title}</div>
      <div className="pxt__filter-dropdown-dates">
        <div className="pxt__filter-dropdown-date-row">
          <label className="pxt__filter-dropdown-date-label" htmlFor="pxt-expires-from">
            От
          </label>
          <input
            id="pxt-expires-from"
            className="pxt__filter-dropdown-date-input"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            aria-label="Дата начала периода истечения"
          />
        </div>
        <div className="pxt__filter-dropdown-date-row">
          <label className="pxt__filter-dropdown-date-label" htmlFor="pxt-expires-to">
            До
          </label>
          <input
            id="pxt-expires-to"
            className="pxt__filter-dropdown-date-input"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            aria-label="Дата конца периода истечения"
          />
        </div>
        <label
          className="pxt__filter-dropdown-item pxt__filter-dropdown-item--unlimited"
          htmlFor="pxt-expires-unlimited"
          tabIndex={0}
          onKeyDown={handleUnlimitedKeyDown}
        >
          <input
            id="pxt-expires-unlimited"
            type="checkbox"
            checked={unlimited}
            onChange={handleUnlimitedChange}
            aria-label="Безлимит — прокси без срока истечения"
          />
          <span className="pxt__filter-dropdown-label">Безлимит</span>
        </label>
      </div>
      <div className="pxt__filter-dropdown-actions">
        <button
          type="button"
          className="pxt__filter-dropdown-btn pxt__filter-dropdown-btn--reset"
          onClick={handleReset}
        >
          Сбросить
        </button>
        <button
          type="button"
          className="pxt__filter-dropdown-btn pxt__filter-dropdown-btn--apply"
          onClick={handleApply}
        >
          Применить
        </button>
      </div>
    </div>
  );

  return ReactDOM.createPortal(
    React.cloneElement(dropdownNode, {
      style: {
        position: 'fixed',
        top: dropdownStyle.top,
        left: dropdownStyle.left,
        width: dropdownStyle.width || undefined,
        maxWidth: 'calc(100vw - 16px)',
        maxHeight: dropdownStyle.maxHeight || 'calc(100vh - 16px)',
        transform: 'none',
        overflow: 'hidden',
        marginTop: 0,
        zIndex: 6000,
        visibility: dropdownStyle.visibility,
      },
    }),
    document.body
  );
};

// ==================== PER PAGE DROPDOWN ====================

const PerPageDropdown = ({ value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="pxt__pagination-perpage" ref={ref}>
      <button
        type="button"
        className="pxt__pagination-select"
        onClick={() => setOpen(!open)}
        aria-label={`${value} на странице`}
        aria-expanded={open}
        tabIndex={0}
      >
        <span className="pxt__pagination-select-value">{value}</span>
        <span className="pxt__pagination-select-text"> на странице</span>
        <ChevronDown />
      </button>
      {open && (
        <div className="pxt__pagination-dropdown">
          {options.map((opt) => (
            <button
              key={opt}
              type="button"
              className={`pxt__pagination-option ${opt === value ? 'pxt__pagination-option--active' : ''}`}
              onClick={() => { onChange(opt); setOpen(false); }}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== CLOSE ICON ====================

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const RefreshCheckIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 11C19.7554 8.24073 18.2937 5.74148 16.0001 4.17109C13.7064 2.6007 10.8182 2.10911 8.10887 2.82141" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4 13C4.24456 15.7593 5.70632 18.2585 7.99994 19.8289C10.2936 21.3993 13.1818 21.8909 15.8911 21.1786" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M21 7L20 11L16 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3 17L4 13L8 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ==================== INFO PANEL HELPERS ====================

const getInfoStatusBadge = (value) => {
  if (!value) return <span className="pxt-info__badge pxt-info__badge--gray">Нет данных</span>;
  const v = value.toLowerCase();
  if (v === 'active' || v === 'активен' || v === 'активна') {
    return <span className="pxt-info__badge pxt-info__badge--green">Активен</span>;
  }
  if (v === 'inactive' || v === 'неактивен' || v === 'неактивна') {
    return <span className="pxt-info__badge pxt-info__badge--red">Неактивен</span>;
  }
  return <span className="pxt-info__badge pxt-info__badge--gray">{value}</span>;
};

const getInfoAvailabilityBadge = (value) => {
  if (!value) return <span className="pxt-info__badge pxt-info__badge--gray">Нет данных</span>;
  const v = value.toLowerCase();
  if (v.includes('доступен') && !v.includes('не')) {
    return <span className="pxt-info__badge pxt-info__badge--green">Доступен</span>;
  }
  if (v.includes('недоступ') || v.includes('не доступ')) {
    return <span className="pxt-info__badge pxt-info__badge--red">Недоступен</span>;
  }
  return <span className="pxt-info__badge pxt-info__badge--gray">{value}</span>;
};

const getInfoOperatorBadge = (value) => {
  if (!value) return <span className="pxt-info__line-value">—</span>;
  const v = value.toLowerCase();
  if (v.includes('мтс') || v === 'mts') {
    return <span className="pxt-info__badge" style={{ background: '#fee2e5', color: '#f33f4e' }}>{value}</span>;
  }
  if (v.includes('мегафон') || v === 'megafon') {
    return <span className="pxt-info__badge pxt-info__badge--green">{value}</span>;
  }
  if (v.includes('т2') || v.includes('теле2') || v === 'tele2') {
    return <span className="pxt-info__badge pxt-info__badge--gray">{value}</span>;
  }
  if (v.includes('билайн') || v === 'beeline') {
    return <span className="pxt-info__badge pxt-info__badge--yellow">{value}</span>;
  }
  return <span className="pxt-info__badge pxt-info__badge--gray">{value}</span>;
};

const getInfoBoolBadge = (value, trueLabel = 'Вкл', falseLabel = 'Выкл') => {
  if (value === true || value === 'Вкл' || value === 'on' || value === '1' || value === 1) {
    return <span className="pxt-info__badge pxt-info__badge--green">{trueLabel}</span>;
  }
  if (value === false || value === 'Выкл' || value === 'off' || value === '0' || value === 0) {
    return <span className="pxt-info__badge pxt-info__badge--red">{falseLabel}</span>;
  }
  if (value === null || value === undefined) {
    return <span className="pxt-info__badge pxt-info__badge--gray">Нет данных</span>;
  }
  return <span className="pxt-info__line-value">{String(value)}</span>;
};

const formatInfoSpeedMbpsPair = (dlMbps, ulMbps) => {
  const dlStr =
    dlMbps != null && Number.isFinite(Number(dlMbps)) ? Number(dlMbps).toFixed(2) : '—';
  const ulStr =
    ulMbps != null && Number.isFinite(Number(ulMbps)) ? Number(ulMbps).toFixed(2) : '—';
  return `${dlStr} Мб/с · ${ulStr} Мб/с`;
};

// ==================== INFO PANEL COMPONENT ====================

export const ProxyInfoPanel = ({
  proxy,
  onClose,
  onToggle,
  onEdit,
  onRefreshCheck,
  onAddToLocationCheck = null,
  isChecking,
  osfpList = [],
}) => {
  const orig = proxy ? (proxy._original || proxy) : null;
  const proxyIdForSpeed = orig
    ? Number(orig.proxy_id ?? orig.id ?? proxy?.id)
    : NaN;
  const canSpeedMeasure = Number.isFinite(proxyIdForSpeed) && proxyIdForSpeed > 0;

  const [speedBusy, setSpeedBusy] = useState(false);
  const [speedMeasureUi, setSpeedMeasureUi] = useState(() => ({ percent: 0, label: '' }));
  const [speedMaxMbps, setSpeedMaxMbps] = useState(() => ({ dl: null, ul: null }));
  const [speedAvgMbps, setSpeedAvgMbps] = useState(() => ({ dl: null, ul: null }));
  const [speedMeasureError, setSpeedMeasureError] = useState(null);
  const speedAbortRef = useRef(null);

  const modemIdForRf = useMemo(() => {
    if (!orig) return null;
    const sid = orig.server_id ?? orig.kraken_server_id ?? orig.modem_id;
    const n = Number(sid);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [orig]);
  const modemModelForRf = useMemo(() => {
    if (!orig) return '';
    return String(orig.modem_type || orig.modem_model || '').trim();
  }, [orig]);
  const lteBandModelSupport = useMemo(
    () => isLteBandSupportedModemModel(modemModelForRf),
    [modemModelForRf]
  );
  const canRfMetrics = modemIdForRf != null && lteBandModelSupport !== false;
  const rfUnsupportedReason = lteBandModelSupport === false
    ? `LTE Band / RF только для ZTE. Модель: ${modemModelForRf || 'unknown'}`
    : null;

  const [rfBusy, setRfBusy] = useState(false);
  const [rfMetrics, setRfMetrics] = useState(null);
  const [rfError, setRfError] = useState(null);
  const [rfSelectedBands, setRfSelectedBands] = useState(DEFAULT_RF_BANDS);
  const [rfBandApplying, setRfBandApplying] = useState(false);
  const [rfBandError, setRfBandError] = useState(null);
  const rfApplyingRef = useRef(false);

  const rfBandOptions = useMemo(() => {
    const fromApi = rfMetrics?.available_bands;
    if (Array.isArray(fromApi) && fromApi.length) {
      return fromApi.map((item) => ({
        value: item.band,
        label: item.band,
        disabled: Boolean(item.disabled),
        disabledReason: item.disabled_reason || undefined,
      }));
    }
    return DEFAULT_LTE_BAND_OPTIONS;
  }, [rfMetrics]);

  useEffect(() => {
    return () => {
      if (speedAbortRef.current) {
        speedAbortRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (speedAbortRef.current) {
      speedAbortRef.current.abort();
      speedAbortRef.current = null;
    }
    setSpeedBusy(false);
    setSpeedMeasureUi({ percent: 0, label: '' });
    setSpeedMaxMbps({ dl: null, ul: null });
    setSpeedAvgMbps({ dl: null, ul: null });
    setSpeedMeasureError(null);
  }, [proxyIdForSpeed]);

  useEffect(() => {
    setRfBusy(false);
    setRfMetrics(null);
    setRfError(null);
    setRfSelectedBands(DEFAULT_RF_BANDS);
    setRfBandApplying(false);
    setRfBandError(null);
    rfApplyingRef.current = false;
  }, [modemIdForRf]);

  useEffect(() => {
    if (!rfMetrics || rfApplyingRef.current) return;
    if (Array.isArray(rfMetrics.bands_enabled) && rfMetrics.bands_enabled.length) {
      setRfSelectedBands(rfMetrics.bands_enabled.filter((b) => b !== 'B20'));
    } else if (Array.isArray(rfMetrics.allowed_lte_bands) && rfMetrics.allowed_lte_bands.length) {
      setRfSelectedBands(rfMetrics.allowed_lte_bands.filter((b) => b !== 'B20'));
    }
  }, [rfMetrics]);

  const handleSpeedMeasureClick = useCallback(async () => {
    if (!canSpeedMeasure) {
      return;
    }
    if (speedAbortRef.current) {
      speedAbortRef.current.abort();
    }
    const ac = new AbortController();
    speedAbortRef.current = ac;
    setSpeedBusy(true);
    setSpeedMeasureError(null);
    setSpeedMeasureUi({ percent: 0, label: 'Подготовка…' });
    try {
      const result = await runAdminCheckerSpeedProbe(
        { proxy_id: proxyIdForSpeed },
        {
          signal: ac.signal,
          onProgress: (ui) => setSpeedMeasureUi(ui),
        }
      );
      if (result.error) {
        setSpeedMeasureError(String(result.error));
        setSpeedMaxMbps({ dl: null, ul: null });
        setSpeedAvgMbps({ dl: null, ul: null });
      } else {
        setSpeedMaxMbps({
          dl: result.downloadMbpsMax ?? null,
          ul: result.uploadMbpsMax ?? null,
        });
        setSpeedAvgMbps({
          dl: result.downloadMbpsAvg ?? null,
          ul: result.uploadMbpsAvg ?? null,
        });
      }
    } catch (e) {
      if (e && e.name === 'AbortError') {
        return;
      }
      setSpeedMeasureError(e instanceof Error ? e.message : 'Ошибка замера');
      setSpeedMaxMbps({ dl: null, ul: null });
      setSpeedAvgMbps({ dl: null, ul: null });
    } finally {
      if (speedAbortRef.current === ac) {
        speedAbortRef.current = null;
      }
      setSpeedBusy(false);
    }
  }, [canSpeedMeasure, proxyIdForSpeed]);

  const handleRfMetricsClick = useCallback(async () => {
    if (!canRfMetrics || rfApplyingRef.current) return;
    setRfBusy(true);
    setRfError(null);
    try {
      const res = await apiService.getModemRadioMetrics(modemIdForRf);
      const data = res?.data?.data || res?.data || null;
      setRfMetrics(data);
    } catch (e) {
      setRfError(extractApiError(e, 'Ошибка RF метрик'));
      setRfMetrics(null);
    } finally {
      setRfBusy(false);
    }
  }, [canRfMetrics, modemIdForRf]);

  const toggleRfBand = useCallback((band, disabled) => {
    if (disabled || rfApplyingRef.current) return;
    setRfSelectedBands((prev) => {
      if (prev.includes(band)) {
        if (prev.length <= 1) return prev;
        return prev.filter((b) => b !== band);
      }
      return [...prev, band];
    });
  }, []);

  const handleRfBandApply = useCallback(async () => {
    if (!canRfMetrics || !rfSelectedBands.length) return;
    rfApplyingRef.current = true;
    setRfBandApplying(true);
    setRfBandError(null);
    setRfError(null);
    try {
      const res = await apiService.setModemBandPolicy(modemIdForRf, rfSelectedBands);
      const data = res?.data?.data || res?.data || null;
      if (data) setRfMetrics(data);
    } catch (e) {
      setRfBandError(extractApiError(e, 'Ошибка band policy'));
      // lock мог примениться несмотря на race verify — перечитаем RF
      try {
        rfApplyingRef.current = false;
        const res = await apiService.getModemRadioMetrics(modemIdForRf);
        const data = res?.data?.data || res?.data || null;
        if (data) setRfMetrics(data);
      } catch {
        // ignore secondary read
      }
    } finally {
      rfApplyingRef.current = false;
      setRfBandApplying(false);
    }
  }, [canRfMetrics, modemIdForRf, rfSelectedBands]);

  if (!proxy) return null;

  const isProxyActiveInDb =
    orig.is_active === true || orig.is_active === 1;

  // Статус = включён/выключен в БД (как колонка/точка в таблице), не путать с «Доступность» из проверки
  const status = (() => {
    if (orig.is_active !== undefined && orig.is_active !== null) {
      return orig.is_active === true || orig.is_active === 1 ? 'Активен' : 'Неактивен';
    }
    return orig.status || (orig.availability_status === 'Доступен' ? 'Активен' : 'Неактивен');
  })();
  const client = orig.client_login || proxy.client || '—';
  const tariff = orig.tariff_plan || proxy.tariff || '—';
  const slimiterRaw = Number(orig.slimiter) || 0;
  const speed = slimiterRaw > 0 ? `${Math.round((slimiterRaw * 8 / 1000) * 100) / 100} Мб/с` : null;
  const parseExpiryDate = (raw) => parseMskInstant(raw);

  const expiryDateObj = parseExpiryDate(orig.expires_at);
  const expiryLineCompact = expiryDateObj
    ? formatProxyExpiresAtInfoPanelCompact(orig.expires_at)
    : null;

  const daysRemaining = (() => {
    if (!expiryDateObj) return null;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const expiry = new Date(expiryDateObj);
    expiry.setHours(0, 0, 0, 0);
    return Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
  })();

  const daysLabelCompact = formatProxyExpiryRemainingCompact(daysRemaining);

  const isExpired = daysRemaining !== null && daysRemaining < 0;
  const isExpiringSoon = daysRemaining !== null && daysRemaining >= 0 && daysRemaining <= 3;
  const availability = orig.availability_status || proxy.availability || 'Нет данных';
  const availabilityLower = (orig.availability_status || proxy.availability || '').toLowerCase();
  const isAvailabilityCheckedUnavailable =
    availabilityLower.includes('недоступ') || availabilityLower.includes('не доступ');
  const isAvailabilityCheckedAvailable =
    availabilityLower === 'available' ||
    (availabilityLower.includes('доступен') && !availabilityLower.includes('не'));
  const lastCheck = orig.last_checked || null;
  const verified = (() => {
    if (!lastCheck) {
      return 'Не проверен';
    }

    const parsedDate = parseMskInstant(lastCheck);
    if (!parsedDate) {
      return 'Не проверен';
    }

    const datePart = parsedDate.toLocaleDateString('ru-RU', {
      timeZone: 'Europe/Moscow',
      day: '2-digit',
      month: '2-digit'
    });
    const timePart = parsedDate.toLocaleTimeString('ru-RU', {
      timeZone: 'Europe/Moscow',
      hour: '2-digit',
      minute: '2-digit'
    });
    return `${datePart} ${timePart}`;
  })();
  /** Внешний IP с быстрой проверки; иначе fallback на адрес в строке подключения (часто внутренний IP модема). */
  const ipFromCheck = (() => {
    const raw = orig.quick_check_ip;
    if (raw != null && String(raw).trim() !== '') return String(raw).trim();
    if (orig.checks && typeof orig.checks === 'object') {
      for (const check of Object.values(orig.checks)) {
        if (!check || typeof check !== 'object') continue;
        const ext = check.external_ip ?? check.details?.external_ip;
        if (ext != null && String(ext).trim() !== '') return String(ext).trim();
      }
    }
    return null;
  })();

  const ipAddress = (() => {
    if (isAvailabilityCheckedUnavailable) {
      return '—';
    }
    if (isAvailabilityCheckedAvailable) {
      return ipFromCheck || '—';
    }
    return ipFromCheck || orig.server_ip || 'Нет данных';
  })();
  const city = orig.selected_city || proxy.geo || '—';
  const server = orig.server_name || proxy.server || '—';
  const modem = orig.modem_name || proxy.modem || '—';
  const port = orig.port || proxy.port || '—';
  const operator = orig.selected_operator || proxy.operator || '';
  const rotationType = orig.modem_reconnect_type || proxy.rotation || '';
  const rotationTimer = orig.modem_reconnect_interval ? `${orig.modem_reconnect_interval}s` : '';
  const maxconn = orig.maxconn !== undefined && orig.maxconn !== null ? orig.maxconn : '—';
  const rlimiter = orig.rlimiter !== undefined && orig.rlimiter !== null ? orig.rlimiter : 0;
  const keepAlive = orig.is_keepalive;
  const gzip = orig.is_compression;
  // Только модификаторы протокола (DF82): Keep-Alive и gzip — у HTTP, UDP — у SOCKS5
  const modifiers = proxyModifiersForType(orig.proxy_type);
  const guard = orig.auto_replace_enabled !== undefined && orig.auto_replace_enabled !== null
    ? orig.auto_replace_enabled
    : proxy.guard;
  const osFingerprintLine = resolveOsFingerprintColumnDisplay(orig, osfpList);

  return (
    <div className="pxt-info" role="complementary" aria-label="Информация о прокси">
      {/* Header */}
      <div className="pxt-info__header">
        <h2 className="pxt-info__title">Информация о прокси</h2>
        <button
          type="button"
          className="pxt-info__close-btn"
          onClick={onClose}
          aria-label="Закрыть панель"
          tabIndex={0}
        >
          <CloseIcon />
        </button>
      </div>

      <div className="pxt-info__body">
        {/* Section 1: Статус, Клиент, Тариф, Скорость, Истекает */}
        <div className="pxt-info__section">
          <div className="pxt-info__line">
            <span className="pxt-info__line-label">Статус</span>
            {getInfoStatusBadge(status)}
          </div>
          <div className="pxt-info__line">
            <span className="pxt-info__line-label">Клиент</span>
            <span className="pxt-info__line-value">{client}</span>
          </div>
          <div className="pxt-info__line">
            <span className="pxt-info__line-label">Тариф</span>
            {tariff && tariff !== '—' ? (
              <span className="pxt-info__badge pxt-info__badge--gray">{tariff}</span>
            ) : (
              <span className="pxt-info__line-value">—</span>
            )}
          </div>
          <div className="pxt-info__line">
            <span className="pxt-info__line-label">Скорость</span>
            {speed ? (
              <span className="pxt-info__badge pxt-info__badge--gray">{speed}</span>
            ) : (
              <span className="pxt-info__line-value">—</span>
            )}
          </div>
          <div className="pxt-info__line">
            <span className="pxt-info__line-label">Истекает</span>
            <span className="pxt-info__line-value">
              {!expiryLineCompact ? (
                '—'
              ) : (
                <>
                  {expiryLineCompact}
                  {daysLabelCompact ? (
                    <>
                      {' '}
                      <span
                        className={`pxt-info__days-badge ${isExpired ? 'pxt-info__days-badge--expired' : ''} ${isExpiringSoon ? 'pxt-info__days-badge--soon' : ''}`}
                      >
                        {daysLabelCompact}
                      </span>
                    </>
                  ) : null}
                </>
              )}
            </span>
          </div>
        </div>

        {/* Section 2: Доступность, Проверен, IP */}
        <div className="pxt-info__section">
          <div className="pxt-info__line">
            <span className="pxt-info__line-label">Страж</span>
            {getInfoBoolBadge(guard)}
          </div>
          <div className="pxt-info__line">
            <span className="pxt-info__line-label">Доступность</span>
            {getInfoAvailabilityBadge(availability)}
          </div>
          <div className="pxt-info__line">
            <span className="pxt-info__line-label">Проверен</span>
            <span className="pxt-info__line-value">{verified}</span>
            {onRefreshCheck && (
              <button
                type="button"
                className={`pxt-info__check-btn${isChecking ? ' pxt-info__check-btn--loading' : ''}`}
                onClick={() => onRefreshCheck(proxy)}
                aria-label={isChecking ? 'Проверка...' : 'Проверить прокси'}
                tabIndex={0}
                disabled={isChecking}
              >
                <RefreshCheckIcon />
              </button>
            )}
          </div>
          <div className="pxt-info__line">
            <span className="pxt-info__line-label">IP</span>
            <span
              className="pxt-info__line-value"
              title={
                ipFromCheck
                  ? 'Внешний IP по последней проверке доступности'
                  : isAvailabilityCheckedUnavailable
                    ? 'Внешний IP не получен (прокси недоступен)'
                    : undefined
              }
            >
              {ipAddress}
            </span>
          </div>
        </div>

        {/* Section 3: Город, Сервер, Модем, Порт, Оператор */}
        <div className="pxt-info__section">
          <div className="pxt-info__line">
            <span className="pxt-info__line-label">Город</span>
            <span className="pxt-info__line-value">{city}</span>
          </div>
          <div className="pxt-info__line">
            <span className="pxt-info__line-label">Сервер</span>
            <span className="pxt-info__line-value">{server}</span>
          </div>
          <div className="pxt-info__line">
            <span className="pxt-info__line-label">Модем</span>
            <span className="pxt-info__line-value">{modem}</span>
          </div>
          <div className="pxt-info__line">
            <span className="pxt-info__line-label">Порт</span>
            <span className="pxt-info__line-value">{port}</span>
          </div>
          <div className="pxt-info__line">
            <span className="pxt-info__line-label">Оператор</span>
            {getInfoOperatorBadge(operator)}
          </div>
        </div>

        {/* Section 4: Ротация, TCP, Запросы, Keep Alive и Сжатие Gzip (у HTTP), UDP (у SOCKS5), Отпечаток ОС */}
        <div className="pxt-info__section">
          <div className="pxt-info__line">
            <span className="pxt-info__line-label">Ротация</span>
            {rotationType ? (
              <div className="pxt-info__rotation-value">
                {rotationTimer && (
                  <span className="pxt-info__rotation-timer">{rotationTimer}</span>
                )}
                <span className="pxt-info__badge pxt-info__badge--yellow">{getRotationLabel(rotationType)}</span>
              </div>
            ) : (
              <span className="pxt-info__line-value">—</span>
            )}
          </div>
          <div className="pxt-info__line">
            <span className="pxt-info__line-label">TCP</span>
            <span className="pxt-info__line-value">{maxconn}</span>
          </div>
          <div className="pxt-info__line">
            <span className="pxt-info__line-label">Запросы</span>
            <span className="pxt-info__line-value">{rlimiter}</span>
          </div>
          {modifiers.keepAlive && (
            <div className="pxt-info__line">
              <span className="pxt-info__line-label">Keep Alive</span>
              {getInfoBoolBadge(keepAlive)}
            </div>
          )}
          {modifiers.gzip && (
            <div className="pxt-info__line">
              <span className="pxt-info__line-label">Сжатие Gzip</span>
              {getInfoBoolBadge(gzip)}
            </div>
          )}
          {modifiers.udp && (
            <div className="pxt-info__line">
              <span className="pxt-info__line-label">UDP</span>
              {getInfoBoolBadge(orig.is_udp_enabled)}
            </div>
          )}
          <div className="pxt-info__line">
            <span className="pxt-info__line-label">Отпечаток ОС</span>
            <span className="pxt-info__line-value">{osFingerprintLine}</span>
          </div>

          <div className="pxt-info__speed-block" aria-label="Замер скорости прокси">
            {!speedBusy && speedMeasureError ? (
              <div className="pxt-info__speed-error" role="alert">
                {speedMeasureError}
              </div>
            ) : null}
            {!speedBusy &&
            (speedMaxMbps.dl != null ||
              speedMaxMbps.ul != null ||
              speedAvgMbps.dl != null ||
              speedAvgMbps.ul != null) ? (
              <div className="pxt-info__speed-results">
                <div className="pxt-info__line pxt-info__line--speed-result">
                  <span className="pxt-info__line-label">Макс</span>
                  <span className="pxt-info__line-value pxt-info__speed-pair">
                    {formatInfoSpeedMbpsPair(speedMaxMbps.dl, speedMaxMbps.ul)}
                  </span>
                </div>
                <div className="pxt-info__line pxt-info__line--speed-result">
                  <span className="pxt-info__line-label">Средняя</span>
                  <span className="pxt-info__line-value pxt-info__speed-pair">
                    {formatInfoSpeedMbpsPair(speedAvgMbps.dl, speedAvgMbps.ul)}
                  </span>
                </div>
              </div>
            ) : null}
            {speedBusy ? (
              <div className="pxt-info__speed-progress">
                <AdminSpeedMeasureProgressBar
                  percent={speedMeasureUi.percent}
                  label={speedMeasureUi.label}
                />
              </div>
            ) : (
              <button
                type="button"
                className="pxt-info__speed-btn"
                onClick={handleSpeedMeasureClick}
                disabled={!canSpeedMeasure}
                title={!canSpeedMeasure ? 'Нет идентификатора прокси для замера' : undefined}
                aria-label="Проверка скорости прокси"
                tabIndex={0}
              >
                Проверка скорости
              </button>
            )}
          </div>

          <div className="pxt-info__speed-block" aria-label="RF метрики LTE модема">
            {!rfBusy && (rfError || rfUnsupportedReason) ? (
              <div className="pxt-info__speed-error" role="alert">
                {rfError || rfUnsupportedReason}
              </div>
            ) : null}
            {!rfBusy && rfMetrics ? (
              <div className="pxt-info__speed-results pxt-info__rf-results">
                <div className="pxt-info__line pxt-info__line--speed-result">
                  <span className="pxt-info__line-label">Band</span>
                  <span className="pxt-info__line-value pxt-info__speed-pair">
                    {rfMetrics.wan_active_band || '—'}
                  </span>
                </div>
                <div className={`pxt-info__line pxt-info__line--speed-result pxt-info__rf-metric--${metricQuality('snr', rfMetrics.lte_snr)}`}>
                  <span className="pxt-info__line-label">SNR</span>
                  <span className="pxt-info__line-value pxt-info__speed-pair">
                    {rfMetrics.lte_snr ?? '—'}
                    <span className="pxt-info__rf-hint">{LTE_METRIC_THRESHOLDS.snr.label}</span>
                  </span>
                </div>
                <div className={`pxt-info__line pxt-info__line--speed-result pxt-info__rf-metric--${metricQuality('rsrq', rfMetrics.lte_rsrq)}`}>
                  <span className="pxt-info__line-label">RSRQ</span>
                  <span className="pxt-info__line-value pxt-info__speed-pair">
                    {rfMetrics.lte_rsrq ?? '—'}
                    <span className="pxt-info__rf-hint">{LTE_METRIC_THRESHOLDS.rsrq.label}</span>
                  </span>
                </div>
                <div className={`pxt-info__line pxt-info__line--speed-result pxt-info__rf-metric--${metricQuality('rsrp', rfMetrics.lte_rsrp)}`}>
                  <span className="pxt-info__line-label">RSRP</span>
                  <span className="pxt-info__line-value pxt-info__speed-pair">
                    {rfMetrics.lte_rsrp ?? '—'}
                    <span className="pxt-info__rf-hint">{LTE_METRIC_THRESHOLDS.rsrp.label}</span>
                  </span>
                </div>
              </div>
            ) : null}
            {rfBusy ? (
              <div className="pxt-info__speed-progress">
                <span className="pxt-info__rf-loading">Чтение RF с модема…</span>
              </div>
            ) : (
              <button
                type="button"
                className="pxt-info__speed-btn"
                onClick={handleRfMetricsClick}
                disabled={!canRfMetrics || rfBandApplying}
                title={
                  rfUnsupportedReason
                    || (!modemIdForRf ? 'Нет modem_id / server_id у прокси' : undefined)
                }
                aria-label="Сигнал LTE"
                tabIndex={0}
              >
                Сигнал LTE
              </button>
            )}
            {canRfMetrics ? (
              <div className="pxt-info__rf-bands" aria-label="LTE band lock">
                <div className="pxt-info__rf-bands-label">Разрешённые band</div>
                <div className="pxt-info__rf-band-checks" role="group" aria-label="LTE bands">
                  {rfBandOptions.map((opt) => {
                    const checked = rfSelectedBands.includes(opt.value);
                    const disabled = Boolean(opt.disabled) || rfBandApplying || rfBusy;
                    return (
                      <label
                        key={opt.value}
                        className={`pxt-info__rf-band-check${disabled && opt.disabled ? ' pxt-info__rf-band-check--disabled' : ''}${checked ? ' pxt-info__rf-band-check--on' : ''}`}
                        title={opt.disabled ? (opt.disabledReason || 'Недоступно') : undefined}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={disabled}
                          onChange={() => toggleRfBand(opt.value, Boolean(opt.disabled))}
                          aria-label={opt.label}
                        />
                        <span>{opt.label}</span>
                      </label>
                    );
                  })}
                </div>
                {rfBandError ? (
                  <div className="pxt-info__speed-error" role="alert">
                    {rfBandError}
                  </div>
                ) : null}
                <button
                  type="button"
                  className="pxt-info__speed-btn pxt-info__rf-band-apply"
                  onClick={handleRfBandApply}
                  disabled={rfBandApplying || rfBusy || !rfSelectedBands.length}
                  aria-label="Применить band"
                  tabIndex={0}
                >
                  {rfBandApplying ? 'Применение…' : 'Применить band'}
                </button>
              </div>
            ) : null}
          </div>
        </div>

        {/* Action buttons */}
        <div className="pxt-info__actions">
          {onToggle && (
            <button
              type="button"
              className={`pxt-info__action-btn ${
                isProxyActiveInDb
                  ? 'pxt-info__action-btn--disable'
                  : 'pxt-info__action-btn--enable'
              }`}
              onClick={() => onToggle(proxy)}
              aria-label={
                isProxyActiveInDb ? 'Отключить прокси' : 'Включить прокси'
              }
              tabIndex={0}
            >
              {isProxyActiveInDb ? 'Отключить' : 'Включить'}
            </button>
          )}
          {onEdit && (
            <button
              type="button"
              className="pxt-info__action-btn pxt-info__action-btn--edit"
              onClick={() => onEdit(proxy)}
              aria-label="Редактировать прокси"
              tabIndex={0}
            >
              Редактировать
            </button>
          )}
          {onAddToLocationCheck && isLocationCheckProxy(proxy) && (
            <button
              type="button"
              className="pxt-info__action-btn pxt-info__action-btn--edit"
              onClick={() => onAddToLocationCheck(proxy)}
              aria-label="В проверку локаций"
              tabIndex={0}
            >
              В проверку локаций
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== ROW ACTIONS DROPDOWN ====================

const RaCheckIcon = () => (
  <svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13.222 4.667a5.833 5.833 0 1 1-2.1-2.78" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
    <path d="M5.25 7l1.75 1.75L10.5 4.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const RaEditIcon = () => (
  <svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6.417 2.333H3.5a1.167 1.167 0 0 0-1.167 1.167v7a1.167 1.167 0 0 0 1.167 1.167h7A1.167 1.167 0 0 0 11.667 10.5V7.583" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M10.792 1.458a1.237 1.237 0 1 1 1.75 1.75L7.583 8.167 5.25 8.75l.583-2.333 4.959-4.959z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const RaReconnectIcon = () => (
  <svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1.167 2.333v3.5h3.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M2.065 8.75a5.25 5.25 0 1 0 1.27-5.415L1.167 5.833" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const RaPowerOffIcon = () => (
  <svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10.123 3.877A4.667 4.667 0 1 1 3.877 3.877" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
    <path d="M7 1.167V7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
);

const RaPowerOnIcon = () => (
  <svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4.083 7l2.334 2.333L10.5 5.25" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="7" cy="7" r="5.833" stroke="currentColor" strokeWidth="1.2"/>
  </svg>
);

const RaDeleteIcon = () => (
  <svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1.75 3.5h10.5M4.667 3.5V2.333a1.167 1.167 0 0 1 1.166-1.166h2.334a1.167 1.167 0 0 1 1.166 1.166V3.5m1.75 0v8.167a1.167 1.167 0 0 1-1.166 1.166H4.083a1.167 1.167 0 0 1-1.166-1.166V3.5h8.166z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const RaAddToCheckerIcon = () => (
  <svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M2.25 2C1.97386 2 1.75 2.22386 1.75 2.5V10.5C1.75 10.7761 1.97386 11 2.25 11H7.5V12H2.25C1.42157 12 0.75 11.3284 0.75 10.5V2.5C0.75 1.67157 1.42157 1 2.25 1H8.25C9.07843 1 9.75 1.67157 9.75 2.5V6.75H8.75V2.5C8.75 2.22386 8.52614 2 8.25 2H2.25Z"
      fill="currentColor"
    />
    <path
      d="M10.5 7.25H11.5V9H13.25V10H11.5V11.75H10.5V10H8.75V9H10.5V7.25Z"
      fill="currentColor"
    />
  </svg>
);

const clampRowActionsMenuPosition = (btnRect, menuWidth, menuHeight) => {
  const margin = 8;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const w = menuWidth;
  const h = menuHeight;

  let left = btnRect.right;
  left = Math.min(left, vw - margin);
  left = Math.max(left, margin + w);

  let top = btnRect.bottom + 4;
  if (top + h > vh - margin) {
    top = btnRect.top - h - 4;
  }
  top = Math.max(margin, top);
  if (top + h > vh - margin) {
    top = Math.max(margin, vh - margin - h);
  }
  return { top, left };
};

const RowActionsDropdown = ({
  row,
  rowMenuKey,
  openRowActionsKey,
  setOpenRowActionsKey,
  onCheck,
  onAddRowToChecker,
  onAddRowToLocationCheck,
  onEdit,
  onReconnect,
  onToggle,
  onDelete,
}) => {
  const isOpen = openRowActionsKey === rowMenuKey;
  const onOpenChange = useCallback(
    (next) => {
      if (next) setOpenRowActionsKey(rowMenuKey);
      else setOpenRowActionsKey(null);
    },
    [rowMenuKey, setOpenRowActionsKey]
  );

  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const updateMenuPosition = useCallback(() => {
    const btn = btnRef.current;
    const menu = menuRef.current;
    if (!btn || !menu) return;
    const rect = btn.getBoundingClientRect();
    const { width, height } = menu.getBoundingClientRect();
    if (width <= 0 || height <= 0) return;
    setPos(clampRowActionsMenuPosition(rect, width, height));
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return;
    updateMenuPosition();
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (
        btnRef.current && !btnRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) {
        onOpenChange(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, onOpenChange]);

  useEffect(() => {
    if (!isOpen) return;
    const onReposition = () => updateMenuPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [isOpen, updateMenuPosition]);

  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isOpen && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const estW = 220;
      const estH = 240;
      setPos(clampRowActionsMenuPosition(rect, estW, estH));
    }
    onOpenChange(!isOpen);
  };

  const exec = (e, fn) => {
    e.preventDefault();
    e.stopPropagation();
    onOpenChange(false);
    if (fn) fn(row);
  };

  const orig = row._original || row;
  const isActive = orig.is_active;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className="pm-action-dots"
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onClick={handleToggle}
        aria-label="Действия"
        aria-expanded={isOpen}
        aria-controls={isOpen ? `pxt-row-actions-${rowMenuKey}` : undefined}
        tabIndex={0}
      >
        <span className="pm-action-dots__dot" />
        <span className="pm-action-dots__dot" />
        <span className="pm-action-dots__dot" />
      </button>
      {isOpen && ReactDOM.createPortal(
        <div
          ref={menuRef}
          id={`pxt-row-actions-${rowMenuKey}`}
          role="menu"
          className="pm-action-dropdown--portal"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          style={{ top: pos.top, left: pos.left }}
        >
          {onCheck && (
            <button type="button" className="pm-action-dropdown--portal__item" onClick={(e) => exec(e, onCheck)}>
              <RaCheckIcon /> Проверить
            </button>
          )}
          {onAddRowToChecker && (
            <button type="button" className="pm-action-dropdown--portal__item" onClick={(e) => exec(e, onAddRowToChecker)}>
              <RaAddToCheckerIcon /> Добавить в чекер
            </button>
          )}
          {onAddRowToLocationCheck && isLocationCheckProxy(row) && (
            <button
              type="button"
              className="pm-action-dropdown--portal__item"
              onClick={(e) => exec(e, onAddRowToLocationCheck)}
            >
              В проверку локаций
            </button>
          )}
          {onEdit && (
            <button type="button" className="pm-action-dropdown--portal__item" onClick={(e) => exec(e, onEdit)}>
              <RaEditIcon /> Редактировать
            </button>
          )}
          {onReconnect && (
            <button type="button" className="pm-action-dropdown--portal__item" onClick={(e) => exec(e, onReconnect)}>
              <RaReconnectIcon /> Переподключить
            </button>
          )}
          {onToggle && (
            <button
              type="button"
              className="pm-action-dropdown--portal__item"
              style={{ color: isActive ? '#f33f4e' : '#52c41a' }}
              onClick={(e) => exec(e, onToggle)}
            >
              {isActive ? <RaPowerOffIcon /> : <RaPowerOnIcon />}
              {isActive ? 'Отключить' : 'Включить'}
            </button>
          )}
          {(onDelete || onReconnect) && <div className="pm-action-dropdown--portal__divider" />}
          {onDelete && (
            <button type="button" className="pm-action-dropdown--portal__item pm-action-dropdown--portal__item--danger" onClick={(e) => exec(e, onDelete)}>
              <RaDeleteIcon /> Удалить
            </button>
          )}
        </div>,
        document.body
      )}
    </>
  );
};

// ==================== MAIN COMPONENT ====================

const ProxyTable = ({
  data = [],
  loading = false,
  totalCount = 0,
  stats = {},
  onSort = null,
  onFilter = null,
  onClearAllFilters = null,
  onPageChange = null,
  onPageSizeChange = null,
  onSync = null,
  syncActive = false,
  onRefresh = null,
  onExport = null,
  onAdd = null,
  onRowAction = null,
  onSelectionChange = null,
  onDelete = null,
  onBulkDisable = null,
  onEdit = null,
  onRefreshCheck = null,
  checkingProxies = null,
  onReconnect = null,
  onToggle = null,
  onBulkCheck = null,
  bulkChecking = false,
  onAddToChecker = null,
  onAddRowToChecker = null,
  onAddToLocationCheck = null,
  onAddRowToLocationCheck = null,
  onSandbox = null,
  sandboxPanel = null,
  onStats = null,
  statsPanel = null,
  currentPage = 1,
  pageSize = 10,
  sortField = null,
  sortDirection = 'asc',
  activeFilters = {},
  filterChips = {},
  filterOptions = {},
  osfpList = [],
  selectionResetToken = 0,
}) => {
  const navigate = useNavigate();

  const handleCellNavLinkClick = useCallback((event, path) => {
    createSpaLinkClickHandler(navigate, path)(event);
    event.stopPropagation();
  }, [navigate]);

  // Map API filter keys back to column keys so header icons highlight correctly
  const apiToColumnKey = {
    client_login: 'client',
    tariff_plan: 'tariff',
    availability_status: 'availability',
    auto_replace_enabled: 'guard',
    server_name: 'server',
    modem: 'modem',
    port: 'port',
    proxy_type: 'type',
    selected_city: 'geo',
    selected_operator: 'operator',
    modem_osfp: 'osFingerprint',
    modem_reconnect_type: 'rotation',
    is_active: 'status',
    is_keepalive: 'is_keepalive',
    is_compression: 'is_compression',
    expires_at: 'expires_at',
  };

  const normalizedFilters = useMemo(() => {
    const result = {};
    Object.entries(activeFilters).forEach(([key, value]) => {
      if (!value || (Array.isArray(value) && value.length === 0)) return;
      const colKey = apiToColumnKey[key] || key;
      result[colKey] = Array.isArray(value) ? value : [value];
    });
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilters]);

  const activeFilterCount = Object.keys(normalizedFilters).length;

  const handleClearAllFilters = useCallback(() => {
    setActiveAvailChip('all');
    setActiveCityChip(null);
    if (onClearAllFilters) {
      onClearAllFilters();
      return;
    }
    if (!onFilter) return;
    Object.keys(normalizedFilters).forEach((colKey) => {
      onFilter(colKey, []);
    });
  }, [onClearAllFilters, normalizedFilters, onFilter]);

  const [selectedRows, setSelectedRows] = useState(new Set());

  /** Статус прокси в строке таблицы для массового вкл/выкл */
  const isRowProxyActive = useCallback((row) => {
    const r = row?._original || row;
    if (!r || typeof r !== 'object') return true;
    const v = r.is_active;
    if (v === false || v === 0) return false;
    return true;
  }, []);

  /** Режим кнопки массового вкл/выкл: только вкл, только выкл, или смешанный выбор */
  const bulkPowerMode = useMemo(() => {
    if (!onBulkDisable || selectedRows.size === 0) return null;
    const selected = data.filter((_, i) => selectedRows.has(i));
    let hasActive = false;
    let hasInactive = false;
    selected.forEach((row) => {
      if (isRowProxyActive(row)) hasActive = true;
      else hasInactive = true;
    });
    if (hasActive && hasInactive) return 'mixed';
    if (hasActive) return 'disable';
    if (hasInactive) return 'enable';
    return null;
  }, [data, selectedRows, onBulkDisable, isRowProxyActive]);

  const [openFilter, setOpenFilter] = useState(null);
  const [infoProxy, setInfoProxy] = useState(null);
  // Затемнение закрывает карточку прокси только настоящим щелчком (владелец 29.10, DF85)
  const infoBackdrop = useBackdropClose(() => setInfoProxy(null));
  /** Одно открытое меню действий строки: иначе при клике по «⋯» другой строки предыдущее не закрывалось */
  const [openRowActionsKey, setOpenRowActionsKey] = useState(null);
  const tableRef = useRef(null);
  const filterButtonRefs = useRef({});
  const lastClickedRef = useRef(null);
  const resolvedInfoProxy = useMemo(() => {
    if (!infoProxy) return null;
    const infoId = infoProxy.id ?? infoProxy.proxy_id;
    if (infoId === undefined || infoId === null) return infoProxy;
    return (
      data.find((row) => {
        const rowId = row.id ?? row.proxy_id;
        return String(rowId) === String(infoId);
      }) || infoProxy
    );
  }, [data, infoProxy]);

  const osFingerprintFilterModel = useMemo(
    () => buildOsFingerprintFilterModel(filterOptions, data, osfpList),
    [filterOptions, data, osfpList]
  );

  // ---- Header chips data ----
  const proxyTotal = stats.total || totalCount || 0;
  const proxyAvailable = stats.available || 0;
  const proxyUnavailable = stats.unavailable || 0;
  const modemAvailable = stats.modemAvailable || 0;
  const modemUnavailable = stats.modemUnavailable || 0;
  const cities = stats.cities || [];

  // ---- Active chip ----
  const [activeAvailChip, setActiveAvailChip] = useState('all');
  const [activeCityChip, setActiveCityChip] = useState(null);

  // Внешний сигнал для принудительного сброса выделения (после удаления и т.п.)
  useEffect(() => {
    setSelectedRows(new Set());
    lastClickedRef.current = null;
    if (onSelectionChange) onSelectionChange([]);
  }, [selectionResetToken, onSelectionChange]);

  useEffect(() => {
    setOpenRowActionsKey(null);
  }, [currentPage, pageSize, selectionResetToken]);

  // ---- Pagination ----
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const startRow = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endRow = Math.min(currentPage * pageSize, totalCount);

  // ---- Selection ----
  const allSelected = data.length > 0 && selectedRows.size === data.length;
  const someSelected = selectedRows.size > 0 && selectedRows.size < data.length;

  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      setSelectedRows(new Set());
      if (onSelectionChange) onSelectionChange([]);
    } else {
      const all = new Set(data.map((_, i) => i));
      setSelectedRows(all);
      if (onSelectionChange) onSelectionChange(data);
    }
  }, [allSelected, data, onSelectionChange]);

  const handleSelectRow = useCallback((index, event) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);

      if (event?.shiftKey && lastClickedRef.current !== null && lastClickedRef.current !== index) {
        const start = Math.min(lastClickedRef.current, index);
        const end = Math.max(lastClickedRef.current, index);
        const shouldSelect = !prev.has(index);
        for (let i = start; i <= end; i++) {
          if (shouldSelect) {
            next.add(i);
          } else {
            next.delete(i);
          }
        }
      } else {
        if (next.has(index)) {
          next.delete(index);
        } else {
          next.add(index);
        }
      }

      if (onSelectionChange) {
        onSelectionChange(data.filter((_, i) => next.has(i)));
      }
      return next;
    });
    lastClickedRef.current = index;
  }, [data, onSelectionChange]);

  // ---- Sort ----
  const handleSort = useCallback((key) => {
    if (!onSort) return;
    const newDir = sortField === key && sortDirection === 'asc' ? 'desc' : 'asc';
    onSort(key, newDir);
  }, [onSort, sortField, sortDirection]);

  // ---- Filter ----
  const handleFilterApply = useCallback((column, values) => {
    if (!onFilter) return;
    if (column === 'status') {
      const normalizedValues = Array.from(
        new Set(
          values
            .map((val) => normalizeStatusFilterValue(val))
            .filter((val) => val !== null)
        )
      );
      onFilter(column, normalizedValues);
      return;
    }
    if (column === 'guard') {
      const normalizedValues = Array.from(
        new Set(
          values
            .map((val) => normalizeGuardFilterValue(val))
            .filter(Boolean)
        )
      );
      onFilter(column, normalizedValues);
      return;
    }
    if (column === 'osFingerprint') {
      const expanded = values.flatMap((label) => {
        const rawList = osFingerprintFilterModel.labelToRaw.get(String(label));
        return rawList && rawList.length > 0 ? rawList : [label];
      });
      onFilter(column, Array.from(new Set(expanded)));
      return;
    }
    if (column !== 'availability') {
      onFilter(column, values);
      return;
    }

    const normalizedValues = Array.from(
      new Set(
        values
          .map((val) => normalizeAvailabilityFilterValue(val))
          .filter(Boolean)
      )
    );

    // Для "Доступности" разрешаем только одно активное значение,
    // чтобы исключить смешанный фильтр "Доступен + Недоступен".
    const singleAvailabilityValue = normalizedValues.length > 0 ? [normalizedValues[0]] : [];
    onFilter(column, singleAvailabilityValue);
  }, [onFilter, osFingerprintFilterModel]);

  const handleFilterReset = useCallback((column) => {
    if (onFilter) onFilter(column, []);
  }, [onFilter]);

  // ---- Get unique values for filter column ----
  const getColumnValues = useCallback((key) => {
    if (key === 'availability') {
      const baseAvailabilityValues = ['Доступен', 'Недоступен', 'Нет данных'];
      const options = Array.isArray(filterOptions[key]) ? filterOptions[key].map(String) : [];
      return Array.from(new Set([...baseAvailabilityValues, ...options]));
    }
    if (key === 'guard') {
      const normalizedOptions = Array.isArray(filterOptions[key])
        ? filterOptions[key]
          .map((val) => normalizeGuardFilterValue(val))
          .filter(Boolean)
        : [];
      return Array.from(new Set(['Вкл', 'Выкл', ...normalizedOptions]));
    }
    if (key === 'osFingerprint') {
      return osFingerprintFilterModel.options;
    }

    if (Object.prototype.hasOwnProperty.call(filterOptions, key)) {
      const options = Array.isArray(filterOptions[key]) ? filterOptions[key].map(String).filter(Boolean) : [];
      if (options.length > 0) {
        return Array.from(new Set(options)).sort();
      }
    }

    const unique = new Set();
    data.forEach((row) => {
      const val = row[key];
      if (val !== undefined && val !== null && val !== '') {
        unique.add(String(val));
      }
    });
    return Array.from(unique).sort();
  }, [data, filterOptions, osFingerprintFilterModel]);

  // ---- Chip click ----
  const handleAvailChipClick = useCallback((chipId) => {
    if (!onFilter) return;
    if (chipId === activeAvailChip || chipId === 'all') {
      setActiveAvailChip('all');
      onFilter('availability', []);
      return;
    }
    setActiveAvailChip(chipId);
    if (chipId === 'available') {
      onFilter('availability', ['Доступен']);
    } else if (chipId === 'unavailable') {
      onFilter('availability', ['Недоступен']);
    }
  }, [onFilter, activeAvailChip]);

  const handleCityChipClick = useCallback((cityName) => {
    if (!onFilter) return;
    if (activeCityChip === cityName) {
      setActiveCityChip(null);
      onFilter('geo', []);
    } else {
      setActiveCityChip(cityName);
      onFilter('geo', [cityName]);
    }
  }, [onFilter, activeCityChip]);

  // ---- Render cell value ----
  const renderCellValue = (key, value, row) => {
    switch (key) {
      case 'tariff':
        return getTariffBadge(value);
      case 'availability':
        return getAvailabilityBadge(value);
      case 'expires_at': {
        const orig = row?._original || {};
        const text = formatProxyExpiresAtDateOnly(orig.expires_at);
        return text || '—';
      }
      case 'guard':
        return getGuardBadge(value);
      case 'modem': {
        if (!value) return '—';
        const m = value.match(/^(№\s*\d+)\s+(.*)$/);
        if (m) return <><b>{m[1]}</b> {m[2]}</>;
        return value;
      }
      case 'operator':
        return getOperatorBadge(value);
      case 'rotation': {
        const rotationType = row.rotationType || value;
        const rotationTimer = row.rotationTimer || '';
        if (!rotationType) return '—';
        const typeNorm = rotationType.toLowerCase().replace(/[\s_-]+/g, '_');
        const label = getRotationLabel(rotationType);
        let badgeClass = 'pxt__badge--rotation-link';
        if (typeNorm.includes('time_link') || typeNorm.includes('timelink')) {
          badgeClass = 'pxt__badge--rotation-timelink';
        } else if (typeNorm === 'time') {
          badgeClass = 'pxt__badge--rotation-time';
        }
        return (
          <div className="pxt__rotation-content">
            <span className={`pxt__badge ${badgeClass}`}>{label}</span>
            {rotationTimer && (
              <span className="pxt__rotation-timer">{rotationTimer}</span>
            )}
          </div>
        );
      }
      case 'osFingerprint':
        return resolveOsFingerprintColumnDisplay(row?._original || row, osfpList);
      default:
        return value || '—';
    }
  };

  // ---- Render header cell ----
  const renderHeaderCell = (col) => {
    const isActive = sortField === col.key;
    const hasFilter = normalizedFilters[col.key] && normalizedFilters[col.key].length > 0;

    if (col.key === 'expires_at' && col.filterable) {
      return (
        <th
          key={col.key}
          className={`pxt__cell pxt__cell--header pxt__cell--${col.key}`}
        >
          <div className="pxt__header-content">
            <span className="pxt__header-label">{col.label}</span>
            {col.sortable && (
              <button
                type="button"
                className="pxt__header-btn"
                onClick={() => handleSort(col.key)}
                aria-label={`Сортировать по ${col.label}`}
                tabIndex={0}
              >
                <SortIcon active={isActive} direction={isActive ? sortDirection : 'asc'} />
              </button>
            )}
            <div className="pxt__filter-wrapper">
              <button
                type="button"
                className={`pxt__header-btn ${hasFilter ? 'pxt__header-btn--active' : ''}`}
                onClick={() => setOpenFilter(openFilter === col.key ? null : col.key)}
                aria-label={`Фильтр по ${col.label}`}
                tabIndex={0}
                ref={(el) => { filterButtonRefs.current[col.key] = el; }}
              >
                <FilterIcon active={hasFilter} />
              </button>
              {openFilter === col.key && (
                <ExpiresAtFilterDropdown
                  activeFilters={normalizedFilters[col.key] || []}
                  onApply={handleFilterApply}
                  onReset={handleFilterReset}
                  onClose={() => setOpenFilter(null)}
                  triggerEl={filterButtonRefs.current[col.key]}
                  title={col.label}
                />
              )}
            </div>
          </div>
        </th>
      );
    }

    return (
      <th
        key={col.key}
        className={`pxt__cell pxt__cell--header pxt__cell--${col.key}`}
      >
        <div className="pxt__header-content">
          <span className="pxt__header-label">{col.label}</span>
          {col.sortable && (
            <button
              type="button"
              className="pxt__header-btn"
              onClick={() => handleSort(col.key)}
              aria-label={`Сортировать по ${col.label}`}
              tabIndex={0}
            >
              <SortIcon active={isActive} direction={isActive ? sortDirection : 'asc'} />
            </button>
          )}
          {col.filterable && (
            <div className="pxt__filter-wrapper">
              <button
                type="button"
                className={`pxt__header-btn ${hasFilter ? 'pxt__header-btn--active' : ''}`}
                onClick={() => setOpenFilter(openFilter === col.key ? null : col.key)}
                aria-label={`Фильтр по ${col.label}`}
                tabIndex={0}
                ref={(el) => { filterButtonRefs.current[col.key] = el; }}
              >
                <FilterIcon active={hasFilter} />
              </button>
              {openFilter === col.key && (
                <ColumnFilterDropdown
                  column={col.key}
                  title={col.label}
                  values={getColumnValues(col.key)}
                  activeFilters={
                    col.key === 'availability'
                      ? (normalizedFilters[col.key] || [])
                        .map((val) => getAvailabilityFilterDisplayValue(val))
                        .filter(Boolean)
                      : col.key === 'guard'
                        ? (normalizedFilters[col.key] || [])
                          .map((val) => getGuardFilterDisplayValue(val))
                          .filter(Boolean)
                      : col.key === 'osFingerprint'
                        ? Array.from(
                          new Set(
                            (normalizedFilters[col.key] || [])
                              .map((raw) => (
                                osFingerprintFilterModel.rawToLabel.get(String(raw))
                                || resolveOsFingerprintFilterLabel(
                                  raw,
                                  osFingerprintFilterModel.idToNameMap,
                                  data
                                )
                              ))
                              .filter(Boolean)
                          )
                        )
                      : (normalizedFilters[col.key] || [])
                  }
                  onApply={handleFilterApply}
                  onReset={handleFilterReset}
                  onClose={() => setOpenFilter(null)}
                  labelMap={col.key === 'rotation' ? ROTATION_LABELS : {}}
                  triggerEl={filterButtonRefs.current[col.key]}
                />
              )}
            </div>
          )}
        </div>
      </th>
    );
  };

  return (
    <div className="pxt-page">
      {/* ==================== HEADER ==================== */}
      <div className="pxt-header">
        <h1 className="pxt-header__title">прокси</h1>

        <div className="pxt-header__badges">
          {/* Proxy status chips */}
          <div className="pxt-header__badge-group">
            <button
              type="button"
              className={`pxt-header__chip ${activeAvailChip === 'all' ? 'pxt-header__chip--active' : ''}`}
              onClick={() => handleAvailChipClick('all')}
              tabIndex={0}
              aria-label={`Все ${proxyTotal}`}
            >
              <span className="pxt-header__chip-label">Все</span>
              <span className="pxt-header__chip-value">{proxyTotal}</span>
            </button>
            <button
              type="button"
              className={`pxt-header__chip ${activeAvailChip === 'available' ? 'pxt-header__chip--active' : ''}`}
              onClick={() => handleAvailChipClick('available')}
              tabIndex={0}
              aria-label={`Доступно ${proxyAvailable}`}
            >
              <span className="pxt-header__chip-label">Доступно</span>
              <span className="pxt-header__chip-value pxt-header__chip-value--green">{proxyAvailable}</span>
            </button>
            <button
              type="button"
              className={`pxt-header__chip ${activeAvailChip === 'unavailable' ? 'pxt-header__chip--active' : ''}`}
              onClick={() => handleAvailChipClick('unavailable')}
              tabIndex={0}
              aria-label={`Недоступно ${proxyUnavailable}`}
            >
              <span className="pxt-header__chip-label">Недоступно</span>
              <span className="pxt-header__chip-value pxt-header__chip-value--red">{proxyUnavailable}</span>
            </button>
          </div>

          {/* Modems section */}
          <div className="pxt-header__badge-group">
            <div className="pxt-header__divider" />
            <span className="pxt-header__section-label">Модемы</span>
            <a
              href="/network-ports?status=working"
              className="pxt-header__chip"
              onClick={createSpaLinkClickHandler(navigate, '/network-ports?status=working')}
              tabIndex={0}
              aria-label={`Модемы доступно ${modemAvailable}`}
            >
              <span className="pxt-header__chip-label">Доступно</span>
              <span className="pxt-header__chip-value pxt-header__chip-value--green">{modemAvailable}</span>
            </a>
            <a
              href="/network-ports?status=not_working"
              className="pxt-header__chip"
              onClick={createSpaLinkClickHandler(navigate, '/network-ports?status=not_working')}
              tabIndex={0}
              aria-label={`Модемы недоступно ${modemUnavailable}`}
            >
              <span className="pxt-header__chip-label">Недоступно</span>
              <span className="pxt-header__chip-value pxt-header__chip-value--red">{modemUnavailable}</span>
            </a>
          </div>

          {/* Cities section — hidden on mobile */}
          {cities.length > 0 && (
            <div className="pxt-header__badge-group pxt-header__badge-group--cities">
              <div className="pxt-header__divider" />
              <span className="pxt-header__section-label">Города</span>
              {cities.map((city) => (
                <button
                  type="button"
                  className={`pxt-header__chip ${activeCityChip === city.name ? 'pxt-header__chip--active' : ''}`}
                  key={city.name}
                  onClick={() => handleCityChipClick(city.name)}
                  tabIndex={0}
                  aria-label={`${city.name} ${city.count}`}
                >
                  <span className="pxt-header__chip-label">{city.name}</span>
                  <span className="pxt-header__chip-value">{city.count}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="pxt-header__actions">
          {/* Selection actions — visible only when rows are selected */}
          {selectedRows.size > 0 && onBulkDisable && bulkPowerMode && (
            <button
              type="button"
              className={
                bulkPowerMode === 'disable'
                  ? 'pxt-header__btn pxt-header__btn--disable'
                  : bulkPowerMode === 'enable'
                    ? 'pxt-header__btn pxt-header__btn--enable'
                    : 'pxt-header__btn pxt-header__btn--bulk-mixed'
              }
              onClick={() => {
                const selectedData = data.filter((_, i) => selectedRows.has(i));
                onBulkDisable(selectedData);
              }}
              aria-label={
                bulkPowerMode === 'disable'
                  ? `Отключить выбранные прокси в Kraken (${selectedRows.size})`
                  : bulkPowerMode === 'enable'
                    ? `Включить выбранные прокси в Kraken (${selectedRows.size})`
                    : `Смешанный выбор прокси (${selectedRows.size})`
              }
              tabIndex={0}
              data-tooltip={
                bulkPowerMode === 'disable'
                  ? `Отключить в Kraken (${selectedRows.size})`
                  : bulkPowerMode === 'enable'
                    ? `Включить в Kraken (${selectedRows.size})`
                    : 'Выберите только включённые или только отключённые прокси'
              }
            >
              {bulkPowerMode === 'enable' ? <PowerOnHeaderIcon /> : <PowerOffHeaderIcon />}
            </button>
          )}
          {selectedRows.size > 0 && onDelete && (
            <button
              type="button"
              className="pxt-header__btn pxt-header__btn--delete"
              onClick={() => {
                const selectedData = data.filter((_, i) => selectedRows.has(i));
                onDelete(selectedData);
              }}
              aria-label={`Удалить выбранные (${selectedRows.size})`}
              tabIndex={0}
              data-tooltip={`Удалить (${selectedRows.size})`}
            >
              <TrashIcon />
            </button>
          )}
          {selectedRows.size > 0 && onEdit && (
            <button
              type="button"
              className="pxt-header__btn pxt-header__btn--bulk-edit"
              onClick={() => {
                const selectedData = data.filter((_, i) => selectedRows.has(i));
                onEdit(selectedData.length === 1 ? selectedData[0] : selectedData);
              }}
              aria-label={`Редактировать выбранные (${selectedRows.size})`}
              tabIndex={0}
              data-tooltip={`Редактировать (${selectedRows.size})`}
            >
              <EditIcon />
            </button>
          )}
          {selectedRows.size > 0 && onAddToChecker && (
            <button
              type="button"
              className="pxt-header__btn pxt-header__btn--add-checker"
              onClick={() => onAddToChecker()}
              aria-label={`Добавить в чекер (${selectedRows.size})`}
              tabIndex={0}
              data-tooltip={`Добавить в чекер (${selectedRows.size})`}
            >
              <AddToCheckerIcon />
            </button>
          )}
          {selectedRows.size > 0 && onAddToLocationCheck && (
            <button
              type="button"
              className="pxt-header__btn"
              onClick={() => onAddToLocationCheck()}
              aria-label="В проверку локаций"
              tabIndex={0}
              data-tooltip="В проверку локаций (Тест Band)"
            >
              <LocationCheckIcon />
            </button>
          )}
          {selectedRows.size > 0 && (onBulkCheck || onRefreshCheck) && (
            <button
              type="button"
              className={`pxt-header__btn pxt-header__btn--bulk-check${bulkChecking ? ' pxt-header__btn--checking' : ''}`}
              disabled={bulkChecking}
              onClick={() => {
                if (onBulkCheck) {
                  onBulkCheck();
                } else {
                  const selectedData = data.filter((_, i) => selectedRows.has(i));
                  selectedData.forEach((proxy) => onRefreshCheck(proxy));
                }
              }}
              aria-label={`Проверить выбранные (${selectedRows.size})`}
              tabIndex={0}
              data-tooltip={`Проверить (${selectedRows.size})`}
            >
              <BugIcon />
            </button>
          )}
          {onSync && (
            <button
              type="button"
              className={`pxt-header__btn${syncActive ? ' pm-sync-btn--active' : ''}`}
              onClick={onSync}
              disabled={syncActive}
              aria-label="Синхронизировать"
              tabIndex={0}
              data-tooltip="Синхронизировать с Kraken"
            >
              <SyncIcon />
            </button>
          )}
          {onRefresh && (
            <button
              type="button"
              className="pxt-header__btn"
              onClick={onRefresh}
              aria-label="Обновить"
              tabIndex={0}
              data-tooltip="Обновить данные"
            >
              <RefreshIcon />
            </button>
          )}
          {onExport && (
            <button
              type="button"
              className="pxt-header__btn"
              onClick={onExport}
              aria-label="Экспорт"
              tabIndex={0}
              data-tooltip="Экспорт в файл"
            >
              <DownloadIcon />
            </button>
          )}
          {activeFilterCount > 0 && (
            <button
              type="button"
              className="pxt-header__btn pxt-header__btn--clear-filters"
              onClick={handleClearAllFilters}
              aria-label={`Сбросить фильтры (${activeFilterCount})`}
              tabIndex={0}
              data-tooltip={`Сбросить фильтры (${activeFilterCount})`}
            >
              <FilterOffIcon />
            </button>
          )}
          <button
            type="button"
            className={`pxt-header__btn${sandboxPanel ? ' pxt-header__btn--filter-active' : ''}`}
            aria-label="Фильтр"
            tabIndex={0}
            onClick={onSandbox}
            data-tooltip="Панель фильтров"
          >
            <FilterHeaderIcon />
          </button>
          <button
            type="button"
            className={`pxt-header__btn${statsPanel ? ' pxt-header__btn--filter-active' : ''}`}
            aria-label="Статистика"
            tabIndex={0}
            onClick={onStats}
            data-tooltip="Статистика"
          >
            <GridIcon />
          </button>
          {onAdd && (
            <button
              type="button"
              className="pxt-header__btn pxt-header__btn--add"
              onClick={onAdd}
              aria-label="Добавить прокси"
              tabIndex={0}
              data-tooltip="Добавить прокси"
            >
              <PlusIcon />
            </button>
          )}
        </div>
      </div>

      {/* ==================== TABLE CONTENT ==================== */}
      <div className={`pxt-content ${infoProxy ? 'pxt-content--with-info' : ''} ${sandboxPanel ? 'pxt-content--with-sandbox' : ''} ${statsPanel ? 'pxt-content--with-stats' : ''}`}>
        <div className="pxt" ref={tableRef}>
          <div className="pxt__table-wrap">
            <table className="pxt__table">
              <thead>
                <tr className="pxt__row pxt__row--header">
                  <th className="pxt__cell pxt__cell--header pxt__cell--checkbox">
                    <button
                      type="button"
                      className="pxt__checkbox-btn"
                      onClick={handleSelectAll}
                      aria-label="Выбрать все"
                      tabIndex={0}
                    >
                      <CheckboxIcon
                        checked={allSelected}
                        indeterminate={someSelected}
                      />
                    </button>
                  </th>
                  <th className="pxt__cell pxt__cell--header pxt__cell--status">
                    <div className="pxt__header-content">
                      <StatusHeaderIcon />
                      <div className="pxt__filter-wrapper">
                        <button
                          type="button"
                          className={`pxt__header-btn ${normalizedFilters.status && normalizedFilters.status.length > 0 ? 'pxt__header-btn--active' : ''}`}
                          onClick={() => setOpenFilter(openFilter === 'status' ? null : 'status')}
                          aria-label="Фильтр по статусу"
                          tabIndex={0}
                          ref={(el) => { filterButtonRefs.current.status = el; }}
                        >
                          <FilterIcon active={!!(normalizedFilters.status && normalizedFilters.status.length > 0)} />
                        </button>
                        {openFilter === 'status' && (
                          <ColumnFilterDropdown
                            column="status"
                            title="Статус"
                            values={['true', 'false']}
                            activeFilters={
                              (normalizedFilters.status || [])
                                .map((val) => getStatusFilterDisplayValue(val))
                                .filter(Boolean)
                            }
                            onApply={handleFilterApply}
                            onReset={handleFilterReset}
                            onClose={() => setOpenFilter(null)}
                            labelMap={{ 'true': 'Включены', 'false': 'Выключены' }}
                            triggerEl={filterButtonRefs.current.status}
                          />
                        )}
                      </div>
                    </div>
                  </th>
                  {COLUMNS.map(renderHeaderCell)}
                  <th className="pxt__cell pxt__cell--header pxt__cell--actions" />
                </tr>
              </thead>
              <tbody>
                {data.length === 0 && !loading && (
                  <tr className="pxt__row pxt__row--empty">
                    <td
                      className="pxt__cell pxt__cell--empty"
                      colSpan={COLUMNS.length + 3}
                    >
                      Нет данных
                    </td>
                  </tr>
                )}
                {loading && data.length === 0 && (
                  <tr className="pxt__row pxt__row--empty">
                    <td
                      className="pxt__cell pxt__cell--empty"
                      colSpan={COLUMNS.length + 3}
                    >
                      Загрузка...
                    </td>
                  </tr>
                )}
                {data.map((row, rowIndex) => {
                  const isSelected = selectedRows.has(rowIndex);
                  const isInfoActive = infoProxy && (infoProxy.id === row.id);
                  const globalIndex = (currentPage - 1) * pageSize + rowIndex + 1;
                  const rowActionsKey = String(
                    row.id != null && row.id !== '' ? row.id : `p${currentPage}-${rowIndex}`
                  );

                  return (
                    <tr
                      key={row.id || rowIndex}
                      className={`pxt__row ${isSelected ? 'pxt__row--selected' : ''} ${isInfoActive ? 'pxt__row--info-active' : ''}`}
                      onClick={(e) => {
                        // Не открывать info при клике на чекбокс или кнопку действий
                        if (
                          e.target.closest('.pxt__checkbox-btn') ||
                          e.target.closest('.pxt__action-btn') ||
                          e.target.closest('.pxt__cell--actions') ||
                          e.target.closest('.pm-action-dots') ||
                          e.target.closest('.pxt__cell-nav-btn')
                        ) return;
                        setInfoProxy(infoProxy && infoProxy.id === row.id ? null : row);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <td className="pxt__cell pxt__cell--checkbox">
                        <button
                          type="button"
                          className="pxt__checkbox-btn"
                          onClick={(e) => handleSelectRow(rowIndex, e)}
                          aria-label={`Выбрать строку ${globalIndex}`}
                          tabIndex={0}
                        >
                          <CheckboxIcon checked={isSelected} />
                        </button>
                      </td>
                      <td className="pxt__cell pxt__cell--status">
                        <span className={`pxt__status-dot ${isProxyActive(row) ? 'pxt__status-dot--active' : 'pxt__status-dot--inactive'}`} />
                      </td>
                      {COLUMNS.map((col) => {
                        const cellContent = renderCellValue(col.key, row[col.key], row);
                        const clientLogin = col.key === 'client' ? getProxyRowClientLoginForClientsPage(row) : '';
                        const modemNav = col.key === 'modem' ? buildProxyModemNetworkPortsNavigation(row) : null;
                        const simModemKey = col.key === 'operator' ? getProxyKrakenModemNumberForSimManager(row) : '';
                        const extraClass =
                          (clientLogin ? ' pxt__cell--client-clickable' : '') +
                          (modemNav ? ' pxt__cell--modem-clickable' : '') +
                          (simModemKey ? ' pxt__cell--operator-clickable' : '');
                        let inner = cellContent;
                        if (clientLogin) {
                          const clientPath = buildClientsPagePathForProxyRow(row);
                          inner = (
                            <a
                              href={clientPath}
                              className="pxt__cell-nav-btn"
                              onClick={(e) => handleCellNavLinkClick(e, clientPath)}
                              aria-label={`Открыть карточку клиента ${clientLogin}`}
                              tabIndex={0}
                            >
                              {cellContent}
                            </a>
                          );
                        } else if (modemNav) {
                          const modemPath = buildNetworkPortsPathForProxyRow(row);
                          const aria =
                            modemNav.mode === 'server_id'
                              ? `Открыть сетевые порты, модем ID ${modemNav.value}`
                              : `Открыть сетевые порты, модем ${modemNav.value}`;
                          inner = (
                            <a
                              href={modemPath}
                              className="pxt__cell-nav-btn"
                              onClick={(e) => handleCellNavLinkClick(e, modemPath)}
                              aria-label={aria}
                              tabIndex={0}
                            >
                              {cellContent}
                            </a>
                          );
                        } else if (simModemKey) {
                          const simPath = buildSimManagerPathForProxyRow(row);
                          inner = (
                            <a
                              href={simPath}
                              className="pxt__cell-nav-btn"
                              onClick={(e) => handleCellNavLinkClick(e, simPath)}
                              aria-label={`SIM-менеджер: SIM на модеме № ${simModemKey}`}
                              tabIndex={0}
                            >
                              {cellContent}
                            </a>
                          );
                        }
                        return (
                          <td
                            key={col.key}
                            className={`pxt__cell pxt__cell--${col.key}${extraClass}`}
                          >
                            {inner}
                          </td>
                        );
                      })}
                      <td className="pxt__cell pxt__cell--actions">
                        <RowActionsDropdown
                          row={row}
                          rowMenuKey={rowActionsKey}
                          openRowActionsKey={openRowActionsKey}
                          setOpenRowActionsKey={setOpenRowActionsKey}
                          onCheck={onRefreshCheck ? (r) => {
                            setInfoProxy(null);
                            onRefreshCheck(r);
                          } : null}
                          onAddRowToChecker={onAddRowToChecker ? (r) => {
                            setInfoProxy(null);
                            onAddRowToChecker(r);
                          } : null}
                          onAddRowToLocationCheck={onAddRowToLocationCheck ? (r) => {
                            setInfoProxy(null);
                            onAddRowToLocationCheck(r);
                          } : null}
                          onEdit={onEdit ? (r) => {
                            setInfoProxy(null);
                            onEdit(r);
                          } : null}
                          onReconnect={onReconnect ? (r) => {
                            setInfoProxy(null);
                            onReconnect(r);
                          } : null}
                          onToggle={onToggle ? (r) => {
                            setInfoProxy(null);
                            onToggle(r);
                          } : null}
                          onDelete={onDelete ? (r) => {
                            setInfoProxy(null);
                            onDelete(r);
                          } : null}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ==================== PAGINATION ==================== */}
          <div className="pxt__pagination">
            <p className="pxt__pagination-info">
              <span className="pxt__pagination-range">{startRow}-{endRow}</span>
              <span className="pxt__pagination-text"> из </span>
              <span className="pxt__pagination-total">{totalCount}</span>
              <span className="pxt__pagination-text"> записей</span>
            </p>

            <div className="pxt__pagination-pages">
              <button
                type="button"
                className="pxt__pagination-btn"
                disabled={currentPage <= 1}
                onClick={() => onPageChange && onPageChange(1)}
                aria-label="Первая страница"
                tabIndex={0}
              >
                <ChevronDoubleLeft />
              </button>
              <button
                type="button"
                className="pxt__pagination-btn"
                disabled={currentPage <= 1}
                onClick={() => onPageChange && onPageChange(currentPage - 1)}
                aria-label="Предыдущая страница"
                tabIndex={0}
              >
                <ChevronLeft />
              </button>
              <div className="pxt__pagination-current">{currentPage}</div>
              <button
                type="button"
                className="pxt__pagination-btn"
                disabled={currentPage >= totalPages}
                onClick={() => onPageChange && onPageChange(currentPage + 1)}
                aria-label="Следующая страница"
                tabIndex={0}
              >
                <ChevronRight />
              </button>
              <button
                type="button"
                className="pxt__pagination-btn"
                disabled={currentPage >= totalPages}
                onClick={() => onPageChange && onPageChange(totalPages)}
                aria-label="Последняя страница"
                tabIndex={0}
              >
                <ChevronDoubleRight />
              </button>
            </div>

            <PerPageDropdown
              value={pageSize}
              options={[10, 15, 20, 50, 100]}
              onChange={(val) => onPageSizeChange && onPageSizeChange(val)}
            />
          </div>
        </div>

        {/* ==================== INFO PANEL ==================== */}
        {resolvedInfoProxy && (
          <>
            <div
              className="pxt-info-backdrop"
              {...infoBackdrop}
              role="presentation"
            />
            <ProxyInfoPanel
              proxy={resolvedInfoProxy}
              onClose={() => setInfoProxy(null)}
              onToggle={onToggle}
              onEdit={onEdit}
              onRefreshCheck={onRefreshCheck}
              onAddToLocationCheck={onAddRowToLocationCheck}
              osfpList={osfpList}
              isChecking={(() => {
                if (!checkingProxies || !resolvedInfoProxy) return false;
                const pid =
                  resolvedInfoProxy.id ??
                  resolvedInfoProxy._original?.id ??
                  resolvedInfoProxy._original?.proxy_id;
                if (pid === undefined || pid === null) return false;
                const key = String(pid);
                for (const x of checkingProxies) {
                  if (String(x) === key) return true;
                }
                return false;
              })()}
            />
          </>
        )}

        {/* ==================== SANDBOX PANEL ==================== */}
        {sandboxPanel}

        {/* ==================== STATS PANEL ==================== */}
        {statsPanel}
      </div>
    </div>
  );
};

export default ProxyTable;
